// auditpm-v7
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase, AUDITOR_SCOPE_NAMES, resolveScope, resolveScopeCategory } from '../lib/supabase'
import { format, parseISO, differenceInDays, addDays } from 'date-fns'
import { DM_TRAINING_KEYS, COMPETENCE_BASIS_OPTIONS, DM_SCOPE_OPTIONS, COMPETENCY_EVENT_TYPES, eventTypeLabel,
  dmAuthorisationGaps, competenceReviewStatus } from '../lib/personnel'
import { getTemplate, applyTemplate } from './Settings'
import { TAEPP_REQ, TAEPP_LABEL, PRACTICE_ROLES, PRACTICE_AREAS, ROLE_AREA, isTrainerOnly, taeppCycleEnd, nextCycleStart,
  currentTaeppCycle, allTaeppCycles, inTaeppCycle, computeTaepp, taeppInFinalSixMonths, fmtHrs } from '../lib/taepp'
import { toast, failed } from '../lib/toast'
import { oteAction, fileToBase64 } from '../lib/ote'

const FIRM_NAME = () => localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd'
const TRAINING_EVAL_FORM_URL = 'https://form.jotform.com/82962701493462'

// Scope names now come from AUDITOR_SCOPE_NAMES (SCOPE_REGISTRY) — single source of truth
const GRADES = ['Auditor', 'Lead Auditor']

// Canonical approved-scope names for an auditor. SINGLE SOURCE: approved_scopes (the field the editor
// writes); falls back to the legacy audit_types column; every value normalised through the scope
// registry so legacy labels (e.g. "bizSAFE WSH RM Audit") display as their canonical name.
function auditorScopeNames(a) {
  // approved_scopes is the single source of truth (the field the editor writes). No fallback to the
  // legacy audit_types column — that made a deliberately-cleared record still show stale scopes.
  const raw = a.approved_scopes || []
  return [...new Set(raw.map(s => (resolveScope(s)?.name) || s).filter(Boolean))]
}
// A valid (registered + unexpired) WSHO satisfies the CPD maintenance requirement. Single source, so the CPD
// column, profile, summaries, filters and eligibility all agree.
function isWshoValid(auditor) {
  return !!auditor?.wsho_status && !(auditor?.wsho_licence_expiry && parseISO(auditor.wsho_licence_expiry) < new Date())
}

// ── CPD cycle (SAC AO Scheme accreditation) ─────────────────────────────────────────────────────────────
// One company-wide 4-year cycle, anchored on firm_settings.cpd_cycle_start/cpd_cycle_expiry (anniversary-
// based, not calendar-year — e.g. 7 Jan 2023 to 6 Jan 2027; set under Settings). Each cycle-year a non-WSHO
// auditor needs ≥10 Accredited (SDU-point) + ≥10 Non-accredited training hours, computed straight from their
// real auditor_training records (cpd_type + hours + date_start) — no separately-typed totals.
//
// Compliance is CUMULATIVE within the cycle (per SAC's assessor clarification, Sep 2026): by the end of
// cycle-year N an auditor needs 10·N accredited AND 10·N non-accredited hours counted from the cycle start.
// Excess accredited hours therefore satisfy every remaining year of the SAME cycle (40h in Y1 covers the
// accredited limb for Y1–Y4), and genuine accredited surplus (beyond its own 10·N) may offset the
// non-accredited requirement — one-directional; non-accredited can never substitute for accredited. 80
// accredited hours over a cycle thus satisfy both limbs entirely. The sum window is bounded to the current
// cycle, so nothing carries into the next cycle. A cycle-year that closes short can never be backfilled by
// later hours (front-loading is allowed, back-loading is not). Accredited SDU points are counted 1:1 as hours.
function cpdCycleYears(cycleStart) {
  if (!cycleStart) return []
  const start = parseISO(cycleStart)
  const years = []
  for (let i = 0; i < 4; i++) {
    const yStart = new Date(start); yStart.setFullYear(yStart.getFullYear() + i)
    const yEnd = new Date(start); yEnd.setFullYear(yEnd.getFullYear() + i + 1)
    years.push({ index: i + 1, start: yStart, end: yEnd })   // half-open [start, end)
  }
  return years
}

function computeCpdCycle(records, cycleStart) {
  const years = cpdCycleYears(cycleStart)
  if (years.length === 0) return { years: [], state: 'no_cycle', sufficient: false }
  const today = new Date()
  const cycStart = years[0].start
  const recs = (records || []).filter(r => r.date_start)
  const sumBetween = (type, from, to) => recs
    .filter(r => r.cpd_type === type)
    .filter(r => { const d = parseISO(r.date_start); return d >= from && d < to })
    .reduce((s, r) => s + (Number(r.hours) || 0), 0)

  const rows = years.map(yr => {
    // CUMULATIVE totals from the start of THIS cycle through the end of cycle-year N. Because the sum window
    // is bounded to [cycStart, yr.end) it never bleeds into the previous or next cycle, so carry-forward
    // resets automatically at each cycle boundary (SAC: excess does not cross cycles).
    const accCum = sumBetween('Accredited', cycStart, yr.end)
    const nonCum = sumBetween('Non-accredited', cycStart, yr.end)
    // Hours dated within this cycle-year alone — for the per-year display only, not the compliance test.
    const accInYear = sumBetween('Accredited', yr.start, yr.end)
    const nonInYear = sumBetween('Non-accredited', yr.start, yr.end)

    const reqCum = 10 * yr.index                            // 10 accredited + 10 non-accredited per year, cumulative
    // Accredited is allocated to its own requirement FIRST; only the genuine excess can offset non-accredited
    // (SAC: accredited may replace non-accredited, never the reverse). surplusAccred never double-counts.
    const surplusAccred = Math.max(0, accCum - reqCum)
    const offsetUsed = Math.min(surplusAccred, Math.max(0, reqCum - nonCum))   // accredited surplus applied to non-accredited
    const accreditedMet = accCum >= reqCum
    const nonAccreditedMet = (nonCum + surplusAccred) >= reqCum
    const remainingAcc = Math.max(0, reqCum - accCum)                          // accredited shortfall at this checkpoint
    const remainingNon = Math.max(0, reqCum - (nonCum + surplusAccred))        // non-accredited shortfall AFTER substitution

    const elapsed = today >= yr.end
    const current = today >= yr.start && today < yr.end
    return { ...yr, accCum, nonCum, accInYear, nonInYear, reqCum, surplusAccred, offsetUsed,
      remainingAcc, remainingNon, accreditedMet, nonAccreditedMet, met: accreditedMet && nonAccreditedMet, elapsed, current }
  })

  // Compliance is assessed against the LATEST COMPLETED checkpoint only — SAC reviews cumulative CPD achieved
  // up to the point of assessment. There is NO permanent-shortfall memory: an earlier year that closed short
  // but was later made up on the cumulative total is compliant now. N* = count of fully-elapsed cycle-years;
  // before the first year closes nothing is due yet (sufficient).
  const elapsedCount = rows.filter(r => r.elapsed).length
  const binding = elapsedCount > 0 ? rows[elapsedCount - 1] : null
  const sufficient = binding ? binding.met : true
  return { years: rows, state: sufficient ? 'compliant' : 'lapsed', sufficient, checkpoint: elapsedCount, binding }
}

// CPD badge for display. A valid WSHO ⇒ "N/A (WSHO)" (satisfied, never a deficiency). Otherwise it reflects
// the real cycle-year training hours: Met (on track / compliant so far), Partial (current year short so far
// but not yet due), Deficient (a past cycle-year's hours were never made up), No record (no cycle dates set).
function cpdBadge(cycle, wshoValid) {
  if (wshoValid) return { label: 'N/A (WSHO)', color: '#27500a', bg: '#EAF3DE' }
  if (!cycle || cycle.state === 'no_cycle') return { label: 'No record', color: '#791F1F', bg: '#FDECEA' }
  if (cycle.state === 'lapsed') return { label: 'Deficient', color: '#791F1F', bg: '#FDECEA' }
  const cur = cycle.years.find(y => y.current)
  if (cur && !cur.met) return { label: 'Partial', color: '#854F0B', bg: '#FFF3E0' }
  return { label: 'Met', color: '#27500a', bg: '#EAF3DE' }
}

// What is still missing against the 4-year maintenance thresholds, in plain words (e.g. "1 more audit as Lead").
// Works for both baseline mode (effective totals) and records mode (per track). Empty = every threshold met.
function maintShortfalls(auditor, m) {
  const gradeIsLead = auditor?.grade === 'Lead Auditor'
  const plural = (n, w) => `${n} more ${w}${n === 1 ? '' : 's'}`
  const list = (audits, days, lead, pct75ok, prefix) => [
    audits < 4 ? `${prefix}${plural(4 - audits, 'audit')}` : null,
    days < 10 ? `${prefix}${Math.round((10 - days) * 10) / 10} more on-site day${10 - days === 1 ? '' : 's'}` : null,
    gradeIsLead && lead < 2 ? `${prefix}${plural(2 - lead, 'audit')} as Lead` : null,
    !pct75ok ? `${prefix}RM/SHMS ratio below 75%` : null,
  ].filter(Boolean)
  if (m?.mode === 'records') return Object.values(m.tracks || {}).flatMap(tr => list(tr.audits, tr.days, tr.leadAudits, tr.pct75ok, `${tr.track}: `))
  return list(m?.effAudits || 0, m?.effDays || 0, m?.effLead || 0, m?.pct75ok !== false, '')
}
// Still building (cycle not over) but short, with the cycle ending within 12 months — needs QM attention now,
// before it turns into a lapse.
function maintAtRisk(auditor, m) {
  if (!m || m.state !== 'building' || !m.cycleEnd) return false
  const warnFrom = new Date(m.cycleEnd); warnFrom.setFullYear(warnFrom.getFullYear() - 1)
  return new Date() >= warnFrom && maintShortfalls(auditor, m).length > 0
}

const FOUR_YEARS_MS = 4 * 365.25 * 24 * 60 * 60 * 1000
const RM_SHMS_MATCH = (l) => {
  // The CONTROLLED scheme column (written by map_scheme at finalisation AND by the historical backfill) is
  // authoritative — check it FIRST, so e.g. a backfilled ConSASS row typed "ConSASS Audit" counts correctly.
  const s = (l.scheme || '').toLowerCase()
  if (s === 'bizsafe rm' || s === 'consass' || s === 'shms') return true
  if (s === 'other') return false                 // known non-qualifying (ISO / overtime) — never RM/SHMS
  const t = (l.audit_type || '').toLowerCase(); const n = (l.notes || '').toLowerCase()
  return t.includes('bizsafe') || t.includes('construction') || t.includes('metalworking') || t.includes('shipyard') ||
    t.includes('oil') || t.includes('semiconductor') || t.includes('chemical') || t.includes('pharmaceutical') ||
    n.includes('consass') || n.includes('shms')
}

// SINGLE SOURCE for maintenance state + the CPD-or-WSHO limb. Consumed by calcEligibility, the roster row,
// maintGaps and cpdGaps so the Personnel page cannot contradict itself.
//   Effective totals = QM-attested baseline (up to baseline_as_of_date) + AuditPM external audit-log rows
//   recorded AFTER that cutoff (never double-counted). Due-ness is derived from the auditor's APPROVAL DATE
//   (no manually-entered cycle start). Three states:
//     basis_unset — no approval_date on record → cannot assess (NOT lapsed)
//     building    — approved < 4 years ago → maintenance not yet due (Eligible)
//     compliant   — approved ≥ 4 years ago (due) and thresholds met
//     lapsed      — approved ≥ 4 years ago (due) and below a threshold (blocks eligibility)
// Thresholds (4 audits / 10 days / 2-as-Lead / 75%) are unchanged.
function maintenanceStatus(auditor, logs, cpdCycle) {
  const today = new Date()
  const gradeIsLead = auditor?.grade === 'Lead Auditor'
  const asOf = auditor?.baseline_as_of_date ? parseISO(auditor.baseline_as_of_date) : null
  // Maintenance window = the firm's AO accreditation cycle (Settings), the SAME cycle used for CPD/training.
  const cycleStart = cpdCycle?.years?.[0]?.start || null
  const cycleEnd = cpdCycle?.years?.length ? cpdCycle.years[cpdCycle.years.length - 1].end : null
  // The QM-attested baseline counts toward THIS cycle only if it was attested within the cycle (its as-of date
  // sits inside the window). At the next rollover that as-of date falls before the new cycle start, so the
  // baseline drops off automatically and only in-cycle AuditPM audits count — it self-corrects each cycle.
  const baselineInCycle = !!(asOf && cycleStart && cycleEnd && asOf >= cycleStart && asOf < cycleEnd)
  // AuditPM external audits counted this cycle: dated within [cycleStart, cycleEnd). When the baseline is
  // in-cycle, only rows AFTER its as-of date are added (rows up to as-of are already inside the baseline lump).
  const future = (logs || []).filter(l => {
    if (!l.audit_date || !l.is_external) return false
    const d = parseISO(l.audit_date)
    if (cycleStart && d < cycleStart) return false
    if (cycleEnd && d >= cycleEnd) return false
    if (baselineInCycle && !(d > asOf)) return false
    return true
  })
  const baseAudits = baselineInCycle ? (parseInt(auditor?.baseline_audits) || 0) : 0
  const baseDays = baselineInCycle ? (parseFloat(auditor?.baseline_auditor_days) || 0) : 0
  const baseLead = baselineInCycle ? (parseInt(auditor?.baseline_lead_audits) || 0) : 0
  const effAudits = baseAudits + future.length
  const effDays = Math.round((baseDays + future.reduce((s, l) => s + (parseFloat(l.mandays) || 0), 0)) * 10) / 10
  const effLeadClaimed = baseLead + future.filter(l => l.role === 'Lead Auditor').length
  const effLead = gradeIsLead ? effLeadClaimed : 0
  // 75% RM/SHMS ratio — assessed over classifiable AuditPM rows only (baseline audits are QM-attested).
  const rmShms = future.filter(RM_SHMS_MATCH).length
  const pct75ok = effAudits < 4 || future.length === 0 || (rmShms / future.length) >= 0.75
  const pct75val = future.length === 0 ? null : Math.round((rmShms / future.length) * 100)
  // CPD limb: a valid (unexpired) WSHO OR a compliant-so-far CPD cycle satisfies it (alternative pathway).
  const wshoValid = isWshoValid(auditor)
  const cpdSufficient = !!cpdCycle && cpdCycle.sufficient
  const cpdLimbOk = wshoValid || cpdSufficient
  const assessable = baselineInCycle || future.length > 0
  // Due-ness follows the accreditation cycle: building while the cycle runs, assessed once it ends.
  let state
  if (!cycleStart || !cycleEnd) state = 'basis_unset'          // no accreditation cycle set in Settings
  else if (today < cycleEnd) state = 'building'                // cycle in progress → not yet due
  else if (!assessable) state = 'basis_unset'
  else state = (effAudits < 4 || effDays < 10 || (gradeIsLead && effLead < 2) || !pct75ok) ? 'lapsed' : 'compliant'
  const reasons = []
  if (state === 'lapsed') {
    if (effAudits < 4) reasons.push('4-year maintenance: fewer than 4 audits')
    if (effDays < 10) reasons.push('4-year maintenance: fewer than 10 on-site days')
    if (gradeIsLead && effLead < 2) reasons.push('4-year maintenance: fewer than 2 audits as Lead')
    if (!pct75ok) reasons.push('4-year maintenance: RM/SHMS ratio below 75%')
  }
  return { effAudits, effDays, effLead, effLeadClaimed, pct75ok, pct75val, wshoValid, cpdSufficient, cpdLimbOk, state, reasons, cycleStart, cycleEnd, baselineInCycle }
}

// ─────────────────────────────────────────────────────────────────────────────
// PHASE C — records-mode, TRACK-SPECIFIC CT17 maintenance (Option B).
// Only auditors migrated to maintenance_source='records' (Phase D importer) use this path; everyone else
// stays on the baseline totals above, so this is a behaviour-neutral addition until the first import.
//
// Two independent tracks, each with its OWN family of qualifying schemes. Per your CT17 reading:
//   RM maintenance   (App 2): 4 audits / 10 days / 2-as-Lead + 75% are all assessed over RM-family rows
//                             {bizSAFE RM, SHMS, ConSASS}.
//   SHMS maintenance (App 1): the same limbs assessed over SHMS-family rows {SHMS, ConSASS} only —
//                             bizSAFE RM does NOT help the SHMS thresholds or numerator.
// The 75% DENOMINATOR for both tracks is ALL countable external rows in the window (Other included, so the
// remaining ≤25% may be other management-system audits). REVIEW rows are excluded from thresholds,
// numerator and denominator, and surfaced for manual classification.
// ─────────────────────────────────────────────────────────────────────────────
const RM_TRACK_SCHEMES   = ['bizSAFE RM', 'SHMS', 'ConSASS']
const SHMS_TRACK_SCHEMES = ['SHMS', 'ConSASS']

// Authorised maintenance tracks from the auditor's approved scopes (single source of truth): a BIZSAFE scope
// opens the RM track, any SHMS sub-scope opens the SHMS track, ISO opens no CT17-maintained track.
function authorisedTracks(auditor) {
  const cats = new Set(auditorScopeNames(auditor).map(n => resolveScopeCategory(n)).filter(Boolean))
  const tracks = []
  if (cats.has('BIZSAFE')) tracks.push('RM')
  if (cats.has('SHMS')) tracks.push('SHMS')
  return tracks
}

function trackReasons(track, tr, gradeIsLead) {
  const r = []
  if (!tr.limbs.audits) r.push(`${track} maintenance: fewer than 4 ${track}-family audits`)
  if (!tr.limbs.days) r.push(`${track} maintenance: fewer than 10 ${track}-family on-site days`)
  if (gradeIsLead && !tr.limbs.lead) r.push(`${track} maintenance: fewer than 2 ${track}-family audits as Lead`)
  if (!tr.limbs.pct75) r.push(`${track} maintenance: ${track}-family ratio below 75%`)
  return r
}

// One track's counts. `rows` = all countable external rows in the window (REVIEW already excluded); the
// thresholds + numerator use only the track's family subset, the 75% denominator uses every row.
function computeTrack(rows, track, gradeIsLead) {
  const set = track === 'RM' ? RM_TRACK_SCHEMES : SHMS_TRACK_SCHEMES
  const family = rows.filter(l => set.includes(l.scheme))
  const audits = family.length
  const days = Math.round(family.reduce((s, l) => s + (parseFloat(l.mandays) || 0), 0) * 10) / 10
  const leadClaimed = family.filter(l => l.role === 'Lead Auditor').length
  const leadAudits = gradeIsLead ? leadClaimed : 0
  const denom = rows.length
  const pct75val = denom === 0 ? null : Math.round((family.length / denom) * 100)
  const pct75ok = denom < 4 || (family.length / denom) >= 0.75
  const limbs = {
    audits: audits >= 4,
    days: days >= 10,
    lead: !gradeIsLead || leadAudits >= 2,
    pct75: pct75ok,
  }
  const limbsOk = limbs.audits && limbs.days && limbs.lead && limbs.pct75
  return { track, audits, days, leadAudits, leadClaimed, qualifying: family.length, denom, pct75val, pct75ok, limbs, limbsOk }
}

function computeRecordsMaintenance(auditor, logs, cpdCycle) {
  const today = new Date()
  const gradeIsLead = auditor?.grade === 'Lead Auditor'
  // Maintenance window = the firm's AO accreditation cycle (Settings), the SAME cycle used for CPD/training.
  const cycleStart = cpdCycle?.years?.[0]?.start || null
  const cycleEnd = cpdCycle?.years?.length ? cpdCycle.years[cpdCycle.years.length - 1].end : null
  const rows = (logs || []).filter(l => {
    if (!l.is_external || !l.audit_date || l.scheme === 'REVIEW') return false
    const d = parseISO(l.audit_date)
    return (!cycleStart || d >= cycleStart) && (!cycleEnd || d < cycleEnd)
  })
  const assessable = rows.length > 0

  // Authorised tracks come from approved_scopes ONLY. Audit history proves activity, not authorisation,
  // so it must never be used to infer a track. No BIZSAFE/SHMS scope → basis_unset + flag for correction.
  const trackNames = authorisedTracks(auditor)

  // CPD limb — identical rule to the baseline path.
  const wshoValid = isWshoValid(auditor)
  const cpdSufficient = !!cpdCycle && cpdCycle.sufficient
  const cpdLimbOk = wshoValid || cpdSufficient

  const stateOf = (limbsOk) => {
    if (!cycleStart || !cycleEnd) return 'basis_unset'
    if (today < cycleEnd) return 'building'
    if (!assessable) return 'basis_unset'
    return limbsOk ? 'compliant' : 'lapsed'
  }

  const tracks = {}
  trackNames.forEach(t => {
    const tr = computeTrack(rows, t, gradeIsLead)
    tr.state = stateOf(tr.limbsOk)
    tr.reasons = tr.state === 'lapsed' ? trackReasons(t, tr, gradeIsLead) : []
    tracks[t] = tr
  })

  // No authorised track → cannot assess. Never lapsed, never inferred: flag the missing scope for correction.
  if (trackNames.length === 0) {
    return {
      mode: 'records',
      state: 'basis_unset',
      reasons: ['Approved scope missing — unable to determine maintenance track (set a bizSAFE / SHMS approved scope)'],
      wshoValid, cpdSufficient, cpdLimbOk,
      effAudits: 0, effDays: 0, effLead: 0, effLeadClaimed: 0, pct75ok: true, pct75val: null,
      tracks: {}, cycleStart, cycleEnd,
    }
  }

  // Overall state = worst track; the "primary" (binding) track supplies the roster chip's top-line numbers.
  const order = { lapsed: 0, basis_unset: 1, building: 2, compliant: 3 }
  const list = Object.values(tracks).sort((a, b) => order[a.state] - order[b.state])
  const primary = list[0] || null
  const overallState = primary ? primary.state : 'basis_unset'
  const reasons = list.filter(t => t.state === 'lapsed').flatMap(t => t.reasons)

  return {
    mode: 'records',
    state: overallState,
    reasons,
    wshoValid, cpdSufficient, cpdLimbOk,
    // Top-line fields (from the binding track) so the roster chip / gaps summary keep working unchanged.
    effAudits: primary ? primary.audits : 0,
    effDays: primary ? primary.days : 0,
    effLead: primary ? primary.leadAudits : 0,
    effLeadClaimed: primary ? primary.leadClaimed : 0,
    pct75ok: primary ? primary.pct75ok : true,
    pct75val: primary ? primary.pct75val : null,
    tracks, cycleStart, cycleEnd,
  }
}

// Single entry point for every consumer. Baseline auditors (all of them today) keep the exact legacy
// calculation; only maintenance_source='records' auditors use the Option B track logic.
function computeMaintenance(auditor, logs, cpdCycle) {
  if (auditor?.maintenance_source === 'records') return computeRecordsMaintenance(auditor, logs, cpdCycle)
  return maintenanceStatus(auditor, logs, cpdCycle)
}

function calcEligibility(auditor, cpdCycle, logs) {
  if (!auditor) return { eligible: false, reasons: ['No auditor data'] }
  const reasons = []

  // 1. Internal QM approval
  if (auditor.approval_status !== 'Approved') reasons.push('Not approved by QM')

  // 2. Qualification route (CT17 Appendix 1)
  const routeOk = auditor.comp_route === 'A'
    ? auditor.comp_5yr_experience === 'Yes' && auditor.comp_scope_experience === 'Yes'
    : auditor.comp_route === 'B'
    ? auditor.comp_3yr_wsho_experience === 'Yes' && auditor.comp_scope_experience === 'Yes'
    : false
  if (!auditor.comp_route) reasons.push('Qualification route not selected')
  else if (!routeOk) reasons.push(`Route ${auditor.comp_route} requirements not fully confirmed`)



  // 4. WSHO licence not expired (WSHO-currency rule — independent of the CPD alternative below)
  const today = new Date()
  if (auditor.wsho_status && auditor.wsho_licence_expiry && new Date(auditor.wsho_licence_expiry) < today)
    reasons.push('WSHO licence expired')

  // 5-7. CPD + maintenance via the single shared calculation (baseline-aware, cycle-aware, track-aware).
  const ms = computeMaintenance(auditor, logs, cpdCycle)
  // CPD requirement: satisfied by a compliant-so-far CPD cycle OR a valid WSHO (alternative pathway). A
  // missing/deficient CPD cycle no longer passes silently.
  if (!ms.cpdLimbOk) reasons.push('CPD requirement not met (no sufficient CPD record and no valid WSHO)')
  // 4-year maintenance: only a LAPSED cycle blocks. Building History and "basis not established" do not.
  if (ms.state === 'lapsed') reasons.push(...ms.reasons)

  return { eligible: reasons.length === 0, reasons, maintenance: ms }
}

function EligibilityBadge({ eligible, reasons }) {
  return (
    <span title={!eligible ? reasons.join('\n') : 'All checks passed'} style={{ cursor: !eligible ? 'help' : 'default',
      fontSize: 13, padding: '2px 9px', borderRadius: 12, fontWeight: 500,
      background: eligible ? '#EAF3DE' : '#FDECEA', color: eligible ? '#27500a' : '#791F1F' }}>
      {eligible ? '✓ Eligible' : '✗ Not eligible'}
    </span>
  )
}

// Compact per-row "More ▾" menu — consolidates secondary row actions so the Actions column stays uncluttered.
// Closes on outside-click; opens upward for the last row so it is never clipped by the table's overflow.
function RowMoreMenu({ items, dropUp }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block', marginRight: 4 }}>
      <button className="sm" onClick={() => setOpen(o => !o)} style={{ whiteSpace: 'nowrap' }}>More ▾</button>
      {open && (
        <div style={{ position: 'absolute', right: 0, zIndex: 40, minWidth: 150, overflow: 'hidden',
          [dropUp ? 'bottom' : 'top']: '100%', [dropUp ? 'marginBottom' : 'marginTop']: 3,
          background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.14)' }}>
          {items.map(it => (
            <button key={it.label} onClick={() => { setOpen(false); it.onClick() }}
              style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent',
                padding: '9px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--text)', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

function AuditorModal({ auditor, onClose, onSave }) {
  const { profile, role, canApprove } = useAuth()
  const isNew = !auditor?.id
  const [form, setForm] = useState(auditor || {
    name: '', email: '', phone: '', grade: 'Auditor',
    approved_scopes: [],
    wsho_status: false, wsho_licence_expiry: '',
    comp_degree: null, comp_wsho_registered: null,
    comp_5yr_experience: null, comp_3yr_wsho_experience: null,
    comp_route: null, comp_scope_experience: null,
    approval_status: 'Pending', approved_by: '', approved_by_role: '',
    approval_date: '', approval_remarks: '',
    active: true,
    // Roles (one person, multiple roles)
    is_auditor: true, is_decision_maker: false, is_external: false,
    // Office / management & administrative personnel (clause 7.4) — non-auditor staff records
    is_staff: false, staff_designation: '', staff_responsibilities: '',
    // External-auditor register (clause 7.3)
    ext_agreement_on_file: false, ext_not_other_ao: false, ext_sac_notified_date: '', ext_impartiality_note: '',
    // Report Approver authorisation + internal training + competence review
    dm_authorised: false, dm_approved_by: '', dm_approved_date: '', dm_competence_basis: [], dm_scope: [], dm_notes: '',
    train_ct17_fundamentals: false, train_checklist_interpretation: false,
    train_findings_submission: false, train_decision_making: false,
    initial_training_date: '', last_competence_review: '', next_competence_review_due: '',
    retraining_required: false, retraining_notes: '',
    // Structured Annex A competence evaluation (digitised HRD-QR-05 / HRD-QR-06)
    competence_evaluation: null,
  })
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState('profile')
  // Open existing records READ-ONLY (View); QMs flip to edit via the top-right Edit button. New records
  // start editable. This mirrors the Quotations view/edit split and prevents accidental edits (and, notably,
  // accidental re-stamping of approval_date on save — see save()).
  const [editing, setEditing] = useState(isNew)

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function toggleArr(k, v) {
    setForm(f => ({ ...f, [k]: f[k]?.includes(v) ? f[k].filter(x => x !== v) : [...(f[k] || []), v] }))
  }
  function yesNo(k, label, hint) {
    return (
      <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, marginBottom: 8, lineHeight: 1.4 }}>{label}</div>
        {hint && <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>{hint}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          {['Yes', 'No'].map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              padding: '5px 14px', borderRadius: 8, fontSize: 13,
              background: form[k] === opt ? (opt === 'Yes' ? '#EAF3DE' : '#f0f0f0') : 'var(--bg2)',
              border: `0.5px solid ${form[k] === opt ? (opt === 'Yes' ? '#639922' : 'var(--border2)') : 'var(--border)'}`,
              color: form[k] === opt ? (opt === 'Yes' ? '#27500a' : 'var(--text)') : 'var(--text2)',
              fontWeight: form[k] === opt ? 500 : 400 }}>
              <input type="radio" name={k} value={opt} checked={form[k] === opt}
                onChange={() => set(k, opt)} style={{ display: 'none' }} />
              {opt}
            </label>
          ))}
          {form[k] && <button onClick={() => set(k, null)} style={{ fontSize: 13, padding: '4px 8px', border: '1px solid var(--border)', background: 'none', borderRadius: 6, cursor: 'pointer', color: 'var(--text3)' }}>Clear</button>}
        </div>
      </div>
    )
  }

  // Derive qualification route from answers
  const qualifies = form.comp_route === 'A'
    ? form.comp_5yr_experience === 'Yes' && form.comp_scope_experience === 'Yes'
    : form.comp_route === 'B'
    ? form.comp_3yr_wsho_experience === 'Yes' && form.comp_scope_experience === 'Yes'
    : false

  async function save() {
    if (!form.name) { toast('Enter a name', 'warning'); return }
    if (!form.is_auditor && !form.is_decision_maker && !form.is_staff && !form.is_trainer) { toast('Choose at least one role', 'warning', { detail: 'In the Profile tab' }); return }
    setSaving(true)
    const data = { ...form }
    // Mirror the authoritative approved_scopes into the legacy audit_types column so they can never
    // diverge (clearing scopes now clears both). audit_types is otherwise no longer read for display.
    data.audit_types = data.approved_scopes || []
    if ((data.approval_status === 'Approved' || data.approval_status === 'Rejected') && canApprove) {
      data.approved_by = profile?.name || data.approved_by
      data.approved_by_role = role
      // approval_date = the ORIGINAL authorisation date. Stamp it ONLY on the FIRST approval (while it is still
      // blank); NEVER overwrite it afterwards — not on profile/evidence/competency edits, not on history imports,
      // not on a re-save of an already-approved auditor, and not on a rejection. The previous unconditional reset
      // (approval_date = now() on every save) was corrupting the 4-year maintenance due-ness.
      if (data.approval_status === 'Approved' && !data.approval_date) {
        data.approval_date = new Date().toISOString()
      }
    }
    if (!data.wsho_licence_expiry) data.wsho_licence_expiry = null
    if (!data.approval_date) data.approval_date = null
    // Coerce optional date fields ('' → null) and ensure the competence-basis array is well-formed.
    for (const k of ['dm_approved_date', 'initial_training_date', 'last_competence_review', 'next_competence_review_due',
                     'maintenance_cycle_start', 'baseline_as_of_date', 'baseline_verified_at', 'ext_sac_notified_date'])
      if (!data[k]) data[k] = null
    // Baseline numeric fields: blank → null (avoid empty-string inserts on numeric columns).
    for (const k of ['baseline_audits', 'baseline_auditor_days', 'baseline_lead_audits'])
      if (data[k] === '' || data[k] === undefined) data[k] = null
    // TAEPP fields are only sent when present on the record, so saving still works before sql/TAEPP_CPD.sql runs.
    for (const k of ['taepp_cycle_start', 'taepp_cycle_end']) if (k in data && !data[k]) data[k] = null
    if (data.is_trainer && data.taepp_cycle_start && !data.taepp_cycle_end) data.taepp_cycle_end = taeppCycleEnd(data.taepp_cycle_start)
    if (!Array.isArray(data.dm_competence_basis)) data.dm_competence_basis = []
    if (!Array.isArray(data.dm_scope)) data.dm_scope = []
    // Report Approver authorisation must record who/when, mirroring the auditor-approval pattern.
    if (data.dm_authorised && (!data.dm_approved_by || !data.dm_approved_date)) {
      data.dm_approved_by = data.dm_approved_by || profile?.name || ''
      data.dm_approved_date = data.dm_approved_date || new Date().toISOString().slice(0, 10)
    }
    let result
    if (form.id) {
      result = await supabase.from('auditors').update(data).eq('id', form.id).select().maybeSingle()
    } else {
      result = await supabase.from('auditors').insert(data).select().maybeSingle()
    }
    setSaving(false)
    if (!result.error) onSave(result.data)
    else failed("Couldn't save", result.error.message)
  }

  // Qualification (route + competence review + WSHO) is auditor-only — hide it for Report Approvers / office staff.
  // A trainer-only person (e.g. a freelance TAEPP trainer) is outside the AO scheme: no competence eval / approval.
  const trainerOnly = isTrainerOnly(form)
  const coreTabs = [
    { id: 'profile', label: 'Profile' },
    ...(form.is_auditor ? [{ id: 'competency', label: 'Qualification' }] : []),
    ...(!trainerOnly ? [{ id: 'evaluation', label: 'Competence Eval' }, { id: 'approval', label: 'Approval' }] : []),
  ]
  function setTaeppStart(v) {
    // Cycle end follows the start (start + 2y − 1d) unless it was changed by hand.
    setForm(f => ({ ...f, taepp_cycle_start: v,
      taepp_cycle_end: (!f.taepp_cycle_end || f.taepp_cycle_end === taeppCycleEnd(f.taepp_cycle_start)) ? taeppCycleEnd(v) : f.taepp_cycle_end }))
  }
  function startNewTaeppCycle() {
    const start = nextCycleStart(form.taepp_cycle_end)
    if (!window.confirm(`Start a new TAEPP cycle from ${format(parseISO(start), 'd MMM yyyy')}? The current cycle is kept as history.`)) return
    setForm(f => ({ ...f,
      taepp_cycle_history: [{ start: f.taepp_cycle_start, end: f.taepp_cycle_end }, ...(Array.isArray(f.taepp_cycle_history) ? f.taepp_cycle_history : [])],
      taepp_cycle_start: start, taepp_cycle_end: taeppCycleEnd(start) }))
  }
  const tabs = [
    ...coreTabs.map((t, i) => ({ ...t, label: `${i + 1}. ${t.label}` })),
    { id: 'evidence', label: 'Evidence' },
  ]
  // If the active tab isn't available for this role (e.g. Qualification hidden for a non-auditor), fall back to Profile.
  useEffect(() => { if (!tabs.some(t => t.id === tab)) setTab('profile') }, [form.is_auditor, trainerOnly, tab])   // eslint-disable-line

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'Add person' : `${editing ? 'Edit' : ''} ${form.name || 'person'}`.trim()}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isNew && !editing && canApprove && (
              <button className="sm primary" onClick={() => setEditing(true)}>Edit</button>
            )}
            <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              border: 'none', background: 'none', padding: '6px 14px', cursor: 'pointer',
              fontWeight: tab === t.id ? 500 : 400, fontSize: 13,
              borderBottom: tab === t.id ? '2px solid var(--brand)' : '2px solid transparent',
              color: tab === t.id ? 'var(--brand)' : 'var(--text2)', borderRadius: 0,
            }}>{t.label}</button>
          ))}
        </div>

        {/* Setup guide — shows on the Profile tab; numbered order + tick-off + jump-to-tab. Sits OUTSIDE the
            fieldset so its navigation buttons work in read-only view too. */}
        {tab === 'profile' && (() => {
          const ce = form.competence_evaluation || {}
          const steps = [
            { n: 1, label: 'Profile & role', hint: 'name, role, grade, scopes', go: 'profile',
              done: !!form.name && (form.is_auditor || form.is_decision_maker || form.is_staff || form.is_trainer) },
            { n: 5, label: 'TAEPP membership', hint: 'cycle start date (bottom of this page)', go: 'profile', show: !!form.is_trainer,
              done: !!form.taepp_cycle_start },
            { n: 2, label: 'Qualification route', hint: 'Route A or B + experience', go: 'competency', show: form.is_auditor,
              done: !!form.comp_route && qualifies },
            { n: 3, label: 'Competence evaluation', hint: 'Annex A areas + outcome + who assessed/approved', go: 'evaluation', show: !trainerOnly,
              done: !!(ce.outcome && ce.assessed_by && ce.approved_by) },
            { n: 4, label: 'Authorise', hint: form.is_decision_maker ? 'Approval tab — auditor + Report Approver' : 'Approval tab — QM approves', go: 'approval', show: !trainerOnly,
              done: (!form.is_auditor || form.approval_status === 'Approved') && (!form.is_decision_maker || form.dm_authorised) },
          ].filter(s => s.show !== false)
          return (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Setting up this person? Do these in order</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {steps.map((s, i) => (
                  <button key={s.n} type="button" onClick={() => setTab(s.go)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <span style={{ width: 19, height: 19, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                      background: s.done ? '#EAF3DE' : '#fff', color: s.done ? '#27500a' : 'var(--text3)', border: `1px solid ${s.done ? '#639922' : 'var(--border2)'}` }}>{s.done ? '✓' : (i + 1)}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{s.label}</span>
                    <span style={{ fontSize: 13, color: 'var(--text3)' }}>— {s.hint}</span>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>
                That's everything needed to add a person. <strong>Evidence</strong>{form.is_auditor ? ', WSHO' : ''} and anything else can be filled over time — not required to save.
              </div>
            </div>
          )
        })()}

        {/* Read-only view disables every control; the top-right Edit button flips `editing` to re-enable them. */}
        <fieldset disabled={!editing} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>

        {/* Competence Evaluation — digitised ISO/IEC 17021-1 Annex A checklist (HRD-QR-05 / HRD-QR-06). */}
        {tab === 'evaluation' && (() => {
          const ce = form.competence_evaluation || {}
          const setCE = (patch) => set('competence_evaluation', { ...ce, ...patch })
          const AUDITOR_ITEMS = [
            ['business_mgmt', 'Business management practices'],
            ['audit_principles', 'Audit principles, practices & techniques'],
            ['ms_standards', 'Specific MS standards / normative documents'],
            ['ao_processes', "The AO's processes"],
            ['client_sector', "Client's business sector"],
            ['client_products', "Client's products, processes & organisation"],
            ['language', 'Language skills'],
            ['note_taking', 'Note-taking & report-writing'],
            ['presentation', 'Presentation skills'],
            ['interviewing', 'Interviewing skills'],
            ['audit_management', 'Audit-management skills'],
          ]
          const REVIEWER_ITEMS = [
            ['audit_principles', 'Audit principles, practices & techniques'],
            ['ms_standards', 'Specific MS standards / normative documents'],
            ['ao_processes', "The AO's processes"],
            ['client_sector', "Client's business sector"],
          ]
          const OFFICE_ITEMS = [
            ['manday_methodology', 'CT 17 audit-duration (man-day) methodology'],
            ['impartiality', 'Impartiality & confidentiality'],
            ['quotation_process', 'Quotation / commercial process'],
            ['ao_processes', "The AO's processes"],
          ]
          const aud = ce.auditor_annexa || {}
          const rev = ce.reviewer_annexa || {}
          const off = ce.office_annexa || {}
          const chk = (checked, onClick, label) => (
            <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!checked} onChange={onClick} />{label}
            </label>
          )
          return (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>
                ISO/IEC 17021-1 Annex A competence evaluation (digitised HRD-QR-05 / HRD-QR-06). Tick each area demonstrated.
              </div>
              {form.is_auditor && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, margin: '6px 0' }}>Auditor — knowledge &amp; skills (Annex A)</div>
                  {AUDITOR_ITEMS.map(([k, l]) => chk(aud[k], () => setCE({ auditor_annexa: { ...aud, [k]: !aud[k] } }), l))}
                </>
              )}
              {form.is_decision_maker && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}>Report Approver — reviewing audit reports (Annex A A.3)</div>
                  {REVIEWER_ITEMS.map(([k, l]) => chk(rev[k], () => setCE({ reviewer_annexa: { ...rev, [k]: !rev[k] } }), l))}
                </>
              )}
              {form.is_staff && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}>Office / Management — role competence</div>
                  {OFFICE_ITEMS.map(([k, l]) => chk(off[k], () => setCE({ office_annexa: { ...off, [k]: !off[k] } }), l))}
                </>
              )}
              <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }} />
              <div className="field">
                <label>Evaluation outcome</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['competent', 'Competent'], ['not_yet', 'Not yet competent']].map(([v, l]) => (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '5px 14px', borderRadius: 8, fontSize: 13,
                      background: ce.outcome === v ? (v === 'competent' ? '#EAF3DE' : '#f0f0f0') : 'var(--bg2)',
                      border: `0.5px solid ${ce.outcome === v ? (v === 'competent' ? '#639922' : 'var(--border2)') : 'var(--border)'}`,
                      color: ce.outcome === v ? (v === 'competent' ? '#27500a' : 'var(--text)') : 'var(--text2)' }}>
                      <input type="radio" name="ce_outcome" value={v} checked={ce.outcome === v} onChange={() => setCE({ outcome: v })} style={{ display: 'none' }} />{l}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Assessed by</label>
                  <input value={ce.assessed_by || ''} onChange={e => setCE({ assessed_by: e.target.value })} placeholder="Name" /></div>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Assessed date</label>
                  <input type="date" value={ce.assessed_date || ''} onChange={e => setCE({ assessed_date: e.target.value })} /></div>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Reviewed by</label>
                  <input value={ce.reviewed_by || ''} onChange={e => setCE({ reviewed_by: e.target.value })} placeholder="Name (leave blank if not reviewed)" /></div>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Reviewed date</label>
                  <input type="date" value={ce.reviewed_date || ''} onChange={e => setCE({ reviewed_date: e.target.value })} /></div>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Approved by</label>
                  <input value={ce.approved_by || ''} onChange={e => setCE({ approved_by: e.target.value })} placeholder="e.g. Tay Chang Mong (Top Management)" /></div>
                <div className="field" style={{ flex: '1 1 45%' }}><label>Approved date</label>
                  <input type="date" value={ce.approved_date || ''} onChange={e => setCE({ approved_date: e.target.value })} /></div>
              </div>
            </div>
          )
        })()}

        {tab === 'profile' && (
          <>
            {/* Role intent FIRST — a person can be an Auditor, a Report Approver, or both. */}
            <div className="field">
              <label>Roles *</label>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>
                What does this person do? Tick any that apply. Auditor adds grade &amp; approved scopes below; a
                Report Approver is authorised separately under the Approval tab; Office / Management records
                management &amp; administrative personnel (clause 7.4) without auditor fields; Trainer (TAEPP) tracks
                TAEPP CPD and practice hours.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[['is_auditor', 'Auditor'], ['is_decision_maker', 'Report Approver'], ['is_staff', 'Office / Management'], ['is_trainer', 'Trainer (TAEPP)']].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                    padding: '7px 14px', borderRadius: 8, fontWeight: form[k] ? 600 : 400,
                    background: form[k] ? '#EAF3DE' : 'var(--bg2)',
                    border: `0.5px solid ${form[k] ? '#639922' : 'var(--border)'}`,
                    color: form[k] ? '#27500a' : 'var(--text2)' }}>
                    <input type="checkbox" checked={!!form[k]} onChange={() => set(k, !form[k])} style={{ display: 'none' }} />
                    {form[k] ? '✓ ' : '○ '}{l}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid-2">
              <div className="field"><label>Full name *</label><input value={form.name} onChange={e => set('name', e.target.value)} /></div>
              <div className="field"><label>Active</label>
                <select value={form.active ? 'Yes' : 'No'} onChange={e => set('active', e.target.value === 'Yes')}>
                  <option>Yes</option><option>No</option>
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Email</label><input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></div>
              <div className="field"><label>Phone</label><input value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
            </div>
            <div className="field"><label>Internal / external</label>
              <select value={form.is_external ? 'External' : 'Internal'} onChange={e => set('is_external', e.target.value === 'External')} style={{ width: 'auto' }}>
                <option>Internal</option><option>External</option>
              </select>
            </div>

            {/* External-auditor register (clause 7.3). Additive; shown only for external personnel — never for a
                trainer-only person (e.g. a freelance TAEPP trainer), who is not an AO auditor. */}
            {form.is_external && !trainerOnly && (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>External auditor register (CT 17 clause 7.3)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.ext_agreement_on_file} onChange={() => set('ext_agreement_on_file', !form.ext_agreement_on_file)} />
                  Signed written agreement on file (confidentiality, impartiality, comply with AO policies)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.ext_not_other_ao} onChange={() => set('ext_not_other_ao', !form.ext_not_other_ao)} />
                  Attested not to be an external auditor of another AO
                </label>
                <div className="field" style={{ maxWidth: 260, marginTop: 8 }}><label>SAC notified of appointment / change</label>
                  <input type="date" value={form.ext_sac_notified_date || ''} onChange={e => set('ext_sac_notified_date', e.target.value)} /></div>
                <div className="field"><label>Impartiality risk analysis</label>
                  <textarea rows={2} value={form.ext_impartiality_note || ''} onChange={e => set('ext_impartiality_note', e.target.value)} placeholder="Any impartiality risk from using this external auditor, and how it is eliminated or minimised" /></div>
              </div>
            )}

            {/* Office / Management details — non-auditor staff (clause 7.4). Additive; hidden unless the role is ticked. */}
            {form.is_staff && (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Office / Management details</div>
                <div className="field"><label>Designation</label>
                  <input value={form.staff_designation || ''} onChange={e => set('staff_designation', e.target.value)} placeholder="e.g. Managing Director, Administrator" /></div>
                <div className="field"><label>Responsibilities</label>
                  <textarea rows={3} value={form.staff_responsibilities || ''} onChange={e => set('staff_responsibilities', e.target.value)} placeholder="Duties, responsibilities and authorities relevant to the auditing activities" /></div>
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                  Relevant qualifications, training and competence evidence for this person can be attached under the <strong>Evidence</strong> tab.
                </div>
              </div>
            )}

            {/* Auditor details — only when the Auditor role is selected. Hidden (not cleared) otherwise, so
                an existing auditor's grade/scopes are never silently wiped by unticking Auditor. */}
            {form.is_auditor ? (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Auditor details</div>
                <div className="field" style={{ maxWidth: 260 }}><label>Grade</label>
                  <select value={form.grade} onChange={e => set('grade', e.target.value)}>
                    {GRADES.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="field"><label>Approved scopes (for SAC schedule and assignment)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {AUDITOR_SCOPE_NAMES.map(t => (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                        padding: '4px 10px', borderRadius: 6,
                        background: form.approved_scopes?.includes(t) ? '#EAF3DE' : 'var(--bg2)',
                        border: `0.5px solid ${form.approved_scopes?.includes(t) ? '#639922' : 'var(--border)'}`,
                        color: form.approved_scopes?.includes(t) ? '#27500a' : 'var(--text2)' }}>
                        <input type="checkbox" checked={form.approved_scopes?.includes(t) || false}
                          onChange={() => toggleArr('approved_scopes', t)} style={{ display: 'none' }} />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ) : form.is_decision_maker ? (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12, fontSize: 13, color: 'var(--text2)' }}>
                Report Approver only — grade and approved scopes (auditor fields) are not required.
                Authorise this person under the <strong>Approval</strong> tab.
                {(form.grade !== 'Auditor' || (form.approved_scopes || []).length > 0) && (
                  <div style={{ marginTop: 4, color: 'var(--text3)' }}>Existing auditor details are retained (re-tick Auditor to edit them).</div>
                )}
              </div>
            ) : null}

            {/* TAEPP membership — only for trainers. Each trainer has their own 2-year cycle. */}
            {form.is_trainer && (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>TAEPP membership</div>
                <div className="grid-2">
                  <div className="field"><label>Cycle start</label>
                    <input type="date" value={form.taepp_cycle_start || ''} onChange={e => setTaeppStart(e.target.value)} /></div>
                  <div className="field"><label>Cycle end</label>
                    <input type="date" value={form.taepp_cycle_end || ''} onChange={e => set('taepp_cycle_end', e.target.value)} /></div>
                </div>
                <div className="field" style={{ maxWidth: 260 }}><label>Membership no.</label>
                  <input value={form.taepp_membership_no || ''} onChange={e => set('taepp_membership_no', e.target.value)} /></div>
                {form.taepp_cycle_start && form.taepp_cycle_end && (
                  <button type="button" className="sm" onClick={startNewTaeppCycle}>Start new cycle</button>
                )}
                {Array.isArray(form.taepp_cycle_history) && form.taepp_cycle_history.length > 0 && (
                  <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>
                    Previous cycles: {form.taepp_cycle_history.map(c => {
                      try { return `${format(parseISO(c.start), 'd MMM yyyy')} – ${format(parseISO(c.end), 'd MMM yyyy')}` } catch (e) { return '' }
                    }).filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {tab === 'competency' && form.is_auditor && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Select qualification route (CT17 Appendix 1)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { val: 'A', label: 'Route A — Relevant Degree Holder', hint: 'Degree in engineering, physics, chemistry, biochemistry, ergonomics, occupational safety, health or hygiene' },
                  { val: 'B', label: 'Route B — Registered WSH Officer', hint: 'Currently registered as a Workplace Safety and Health Officer' },
                ].map(r => (
                  <label key={r.val} onClick={() => set('comp_route', r.val)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                    background: form.comp_route === r.val ? 'var(--brand-light)' : 'var(--bg2)',
                    border: `1px solid ${form.comp_route === r.val ? 'var(--brand)' : 'var(--border)'}` }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${form.comp_route === r.val ? 'var(--brand)' : 'var(--border2)'}`,
                      background: form.comp_route === r.val ? 'var(--brand)' : 'transparent', flexShrink: 0, marginTop: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {form.comp_route === r.val && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: form.comp_route === r.val ? 500 : 400, color: form.comp_route === r.val ? 'var(--brand-dark)' : 'var(--text)' }}>{r.label}</div>
                      <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>{r.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {form.comp_route === 'A' && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12, fontStyle: 'italic' }}>Route A — confirm experience requirements</div>
                {yesNo('comp_5yr_experience', 'Has the person at least 5 years of practical experience relevant to the work to be done by an auditor?')}
                {yesNo('comp_scope_experience', 'Does the person have practical experience relevant to the audit scope being approved?', 'e.g. construction, manufacturing, marine, chemical/pharma industry experience as applicable')}
                {qualifies && <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: '#EAF3DE', color: '#27500a', fontSize: 13, fontWeight: 500 }}>✓ Route A qualification confirmed</div>}
              </div>
            )}

            {form.comp_route === 'B' && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12, fontStyle: 'italic' }}>Route B — confirm experience requirements</div>
                {yesNo('comp_3yr_wsho_experience', 'Has the person at least 3 years of practical experience relevant to the work to be done by an auditor (from date of WSH Officer registration)?')}
                {yesNo('comp_scope_experience', 'Does the person have practical experience relevant to the audit scope being approved?', 'e.g. construction, manufacturing, marine, chemical/pharma industry experience as applicable')}
                {qualifies && <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: '#EAF3DE', color: '#27500a', fontSize: 13, fontWeight: 500 }}>✓ Route B qualification confirmed</div>}
              </div>
            )}

            {!form.comp_route && (
              <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic', padding: '8px 0' }}>Select a route above to continue.</div>
            )}

            {/* Competence review control — initial training, annual review prompt, retraining flag. */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Competence review</div>
              <div className="grid-2">
                <div className="field"><label>Initial training completed</label>
                  <input type="date" value={form.initial_training_date || ''} onChange={e => set('initial_training_date', e.target.value)} /></div>
                <div className="field"><label>Last competence review</label>
                  <input type="date" value={form.last_competence_review || ''} onChange={e => set('last_competence_review', e.target.value)} /></div>
              </div>
              <div className="grid-2">
                <div className="field"><label>Next review due</label>
                  <input type="date" value={form.next_competence_review_due || ''} onChange={e => set('next_competence_review_due', e.target.value)} />
                  {form.last_competence_review && (
                    <button type="button" className="sm" style={{ marginTop: 4, fontSize: 13 }}
                      onClick={() => set('next_competence_review_due', format(addDays(parseISO(form.last_competence_review), 365), 'yyyy-MM-dd'))}>
                      Set +1 year from last review
                    </button>
                  )}
                </div>
                <div className="field"><label>Retraining required</label>
                  <select value={form.retraining_required ? 'Yes' : 'No'} onChange={e => set('retraining_required', e.target.value === 'Yes')}>
                    <option>No</option><option>Yes</option>
                  </select>
                </div>
              </div>
              {(() => { const r = competenceReviewStatus(form); return r && r.state !== 'ok' ? (
                <div style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, marginBottom: 8,
                  background: r.state === 'overdue' ? '#FDECEA' : '#FFF3E0', color: r.state === 'overdue' ? '#791F1F' : '#854F0B' }}>⚠ {r.label}</div>
              ) : null })()}
              {form.retraining_required && (
                <div className="field"><label>Retraining notes</label>
                  <textarea rows={2} value={form.retraining_notes || ''} onChange={e => set('retraining_notes', e.target.value)}
                    placeholder="What triggered retraining (issue / complaint / observation / standards update)…" /></div>
              )}
            </div>

            {/* WSHO status — folded in from the former Regulatory tab; affects COI checks and scope eligibility. */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>WSHO status</div>
              <div className="field"><label>Is the person a WSHO (Workplace Safety and Health Officer)?</label>
                <select value={form.wsho_status ? 'Yes' : 'No'} onChange={e => set('wsho_status', e.target.value === 'Yes')} style={{ width: 'auto' }}>
                  <option>No</option><option>Yes</option>
                </select>
              </div>
              {form.wsho_status && (
                <div className="field"><label>WSHO licence expiry date</label>
                  <input type="date" value={form.wsho_licence_expiry || ''} onChange={e => set('wsho_licence_expiry', e.target.value)} />
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'approval' && (
          <>
            {/* ───── AUDITOR APPROVAL (Quality Manager) — only when the Auditor role is assigned ───── */}
            {form.is_auditor && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Auditor approval <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text3)' }}>· Quality Manager</span></div>
                <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text2)' }}>Status</span>
                  <span style={{ fontSize: 13, padding: '3px 10px', borderRadius: 12,
                    background: form.approval_status === 'Approved' ? '#EAF3DE' : form.approval_status === 'Rejected' ? '#FDECEA' : '#FFF3E0',
                    color: form.approval_status === 'Approved' ? '#27500a' : form.approval_status === 'Rejected' ? '#791F1F' : '#854F0B' }}>
                    {form.approval_status || 'Pending'}
                  </span>
                  {(() => {
                    // Show the COMPETENCE approver (the authority who judged competence, from the Competence Eval
                    // tab — e.g. Top Management), NOT the logged-in user who activated the record. Falls back to the
                    // legacy approval fields only if no competence-eval sign-off is present.
                    const ce = form.competence_evaluation || {}
                    const who = ce.approved_by || form.approved_by
                    const when = ce.approved_date || form.approval_date
                    return who ? <span style={{ fontSize: 13, color: 'var(--text2)' }}>approved by <strong>{who}</strong>{when && ` · ${new Date(when).toLocaleDateString('en-SG')}`}</span> : null
                  })()}
                </div>
                {!canApprove ? (
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>Only a Quality Manager (Primary or Delegate) can approve or reject an auditor.</div>
                ) : (
                  <>
                    <div className="field"><label>Remarks</label>
                      <textarea value={form.approval_remarks || ''} onChange={e => set('approval_remarks', e.target.value)} rows={2} placeholder="Add remarks for this decision…" /></div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ background: '#EAF3DE', color: '#27500a', border: '1px solid #639922', fontWeight: 500, flex: 1 }} onClick={() => set('approval_status', 'Approved')}>✓ Approve</button>
                      <button style={{ background: '#FDECEA', color: '#791F1F', border: '1px solid #E24B4A', fontWeight: 500, flex: 1 }} onClick={() => set('approval_status', 'Rejected')}>✗ Reject</button>
                    </div>
                    {form.approval_status !== 'Pending' && (
                      <button style={{ marginTop: 8, width: '100%', background: 'var(--bg2)', color: 'var(--text2)', border: '1px solid var(--border2)', fontSize: 13 }}
                        onClick={() => { set('approval_status', 'Pending'); set('approved_by', ''); set('approved_by_role', ''); set('approval_date', ''); set('approval_remarks', '') }}>↩ Reset approval to Pending</button>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>The approver shown is taken from the Competence Eval tab. Saving here activates the record in AuditPM; your action is logged internally.</div>
                  </>
                )}
              </div>
            )}

            {form.is_auditor && form.is_decision_maker && <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0' }} />}

            {/* ───── DECISION-MAKER AUTHORISATION — clean guided flow, only when the DM role is assigned ───── */}
            {form.is_decision_maker ? (() => {
              const gaps = dmAuthorisationGaps(form)           // job-independent requirements
              const remaining = gaps.filter(g => g !== 'Report Approver authorisation not granted')
              const authorised = !!form.dm_authorised && remaining.length === 0
              return (
                <div>
                  {/* A. Header + calm status */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>Report Approver Authorisation</div>
                    <span style={{ fontSize: 13, padding: '3px 12px', borderRadius: 12, fontWeight: 500,
                      background: authorised ? '#EAF3DE' : '#f0f0f0', color: authorised ? '#27500a' : 'var(--text2)' }}>
                      {authorised ? '✓ Authorised' : 'Not authorised yet'}
                    </span>
                  </div>

                  {/* B. Management approval */}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', margin: '4px 0 6px' }}>Management approval</div>
                  <div className="grid-2">
                    <div className="field"><label>Approved by</label>
                      <input value={form.dm_approved_by || ''} onChange={e => set('dm_approved_by', e.target.value)} placeholder="Manager name" /></div>
                    <div className="field"><label>Approved date</label>
                      <input type="date" value={form.dm_approved_date || ''} onChange={e => set('dm_approved_date', e.target.value)} /></div>
                  </div>

                  {/* Authorised scope — what this person may review/approve */}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', margin: '10px 0 6px' }}>Authorised to approve <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(at least one)</span></div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {DM_SCOPE_OPTIONS.map(([k, l]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                        padding: '5px 12px', borderRadius: 8,
                        background: (form.dm_scope || []).includes(k) ? '#EAF3DE' : 'var(--bg2)',
                        border: `0.5px solid ${(form.dm_scope || []).includes(k) ? '#639922' : 'var(--border)'}`,
                        color: (form.dm_scope || []).includes(k) ? '#27500a' : 'var(--text2)' }}>
                        <input type="checkbox" checked={(form.dm_scope || []).includes(k)} onChange={() => toggleArr('dm_scope', k)} style={{ display: 'none' }} />
                        {(form.dm_scope || []).includes(k) ? '✓ ' : ''}{l}
                      </label>
                    ))}
                  </div>

                  {/* C. Competence basis */}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', margin: '10px 0 6px' }}>Competence basis <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(tick at least one)</span></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 8 }}>
                    {COMPETENCE_BASIS_OPTIONS.map(([k, l]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={(form.dm_competence_basis || []).includes(k)} onChange={() => toggleArr('dm_competence_basis', k)} />{l}
                      </label>
                    ))}
                  </div>

                  {/* D. Internal training */}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', margin: '12px 0 6px' }}>Internal training completed</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 8 }}>
                    {DM_TRAINING_KEYS.map(([k, l]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!form[k]} onChange={() => set(k, !form[k])} />{l}
                      </label>
                    ))}
                  </div>

                  {/* E. Notes */}
                  <div className="field" style={{ marginTop: 12 }}><label>Notes (optional)</label>
                    <textarea rows={2} value={form.dm_notes || ''} onChange={e => set('dm_notes', e.target.value)} placeholder="Management justification / basis details…" /></div>

                  {/* F. Single action */}
                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                    {!form.dm_authorised ? (
                      <button className="primary" onClick={() => {
                        set('dm_authorised', true)
                        if (!form.dm_approved_by) set('dm_approved_by', profile?.name || '')
                        if (!form.dm_approved_date) set('dm_approved_date', new Date().toISOString().slice(0, 10))
                      }}>Authorise Report Approver</button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, color: '#27500a', fontWeight: 500 }}>✓ Authorised{form.dm_approved_by ? ` by ${form.dm_approved_by}` : ''}{form.dm_approved_date ? ` · ${new Date(form.dm_approved_date).toLocaleDateString('en-SG')}` : ''}</span>
                        <button className="sm" onClick={() => set('dm_authorised', false)}>Withdraw authorisation</button>
                      </div>
                    )}
                    {form.dm_authorised && remaining.length > 0 && (
                      <div style={{ fontSize: 13, color: '#854F0B', marginTop: 8 }}>Still needed before they can approve a job: {remaining.join(' · ')}.</div>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>A Report Approver can never approve a job they audited.</div>
                  </div>
                </div>
              )
            })() : !form.is_auditor ? (
              <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Assign a role in the Profile tab first.</div>
            ) : null}
          </>
        )}

        {tab === 'evidence' && (
          form.id
            ? <>{form.is_auditor && <AuditorSignature auditorId={form.id} name={form.name} />}<PersonnelEvidence auditorId={form.id} /></>
            : <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic', padding: '8px 0' }}>Save this person first, then re-open the record to attach evidence files.</div>
        )}

        </fieldset>

        <div className="modal-footer">
          <button onClick={onClose}>{editing && canApprove ? 'Cancel' : 'Close'}</button>
          {editing && canApprove && <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Signature for OTE endorsements (Admin only). Placed on MOM's endorsement Excel when THIS auditor signs an OTE
// job. Upload / replace / remove go through ote-actions (checked server-side; the auditor is emailed a copy; the old
// signature is kept on record, never deleted). ──
function AuditorSignature({ auditorId, name }) {
  const { isAdmin } = useAuth()
  const [sig, setSig] = useState(undefined)   // undefined = loading, null = none
  const [busy, setBusy] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => { if (isAdmin && auditorId) load() }, [isAdmin, auditorId])
  async function load() {
    try { const d = await oteAction({ action: 'signature-view', auditorId }); setSig(d.signature || null) }
    catch (e) { setSig(null); failed("Couldn't load the signature", e.message) }
  }
  async function upload(file) {
    if (!file) return
    if (!['image/png', 'image/jpeg'].includes(file.type)) { toast('Please choose a PNG or JPG picture', 'warning'); return }
    if (file.size > 1024 * 1024) { toast('The picture is larger than 1 MB', 'warning'); return }
    if (sig && !window.confirm(`Replace ${name}'s signature? The old one is kept on record.`)) return
    setBusy(true)
    try {
      const d = await oteAction({ action: 'signature-upload', auditorId, mime: file.type, fileBase64: await fileToBase64(file) })
      toast('Signature saved', 'success', { detail: d.emailed ? `${name} has been emailed a copy.` : `${name} has no email address — please tell them.` })
      await load()
    } catch (e) { failed("Couldn't save the signature", e.message) }
    setBusy(false)
  }
  async function remove() {
    if (!window.confirm(`Remove ${name}'s signature? They will not be able to sign OTE endorsements until a new one is added.`)) return
    setBusy(true)
    try { await oteAction({ action: 'signature-remove', auditorId }); toast('Signature removed', 'success'); await load() }
    catch (e) { failed("Couldn't remove the signature", e.message) }
    setBusy(false)
  }

  if (!isAdmin) return null
  return (
    <div className="card" style={{ padding: 12, marginBottom: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Signature (OTE endorsements)</div>
      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>Placed on MOM’s endorsement form when {name || 'this auditor'} signs an OTE job.</div>
      {sig === undefined ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div> : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {sig
            ? <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 6, background: '#fff' }}>
                {sig.url ? <img src={sig.url} alt="Signature" style={{ display: 'block', maxHeight: 60, maxWidth: 220, objectFit: 'contain' }} /> : <span style={{ fontSize: 13 }}>Saved</span>}
              </div>
            : <div style={{ fontSize: 13, color: 'var(--text2)' }}>No signature yet</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; upload(f) }} />
            <button className="sm primary" onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? 'Saving…' : sig ? 'Replace' : 'Upload signature'}</button>
            {sig && <button className="sm" onClick={remove} disabled={busy}>Remove</button>}
          </div>
          {sig && <div style={{ fontSize: 13, color: 'var(--text2)', width: '100%' }}>Added {format(parseISO(sig.uploaded_at), 'd MMM yyyy')}</div>}
        </div>
      )}
    </div>
  )
}

// ── Personnel evidence attachments (private bucket + signed URLs). Admin/QM upload+delete; others view. ──
const EVIDENCE_TYPES = ['Qualification', 'Training Record', 'CPD Evidence', 'Competency Assessment',
  'Witness Assessment', 'Observer Record', 'Report Approver Authorisation Evidence',
  'WSHO Registration / Card', 'WSH Auditor Approval', 'Appointment / Approval Record', 'Other']

function PersonnelEvidence({ auditorId }) {
  const { profile, canApprove, logAction } = useAuth()   // canApprove = QM or Admin
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(null)   // the inline upload form, when adding

  useEffect(() => { if (auditorId) load() }, [auditorId])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('personnel_evidence')
      .select('*').eq('auditor_id', auditorId).order('uploaded_at', { ascending: false })
    if (error) console.warn('personnel_evidence load error (run PERSONNEL_EVIDENCE.sql):', error.message)
    setRows(data || []); setLoading(false)
  }
  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function uploadAndSave() {
    const f = form
    if (!f?.evidence_type) { toast('Choose an evidence type', 'warning'); return }
    if (!f?.file) { toast('Choose a file', 'warning'); return }
    setBusy(true)
    const path = `${auditorId}/${Date.now()}_${f.file.name.replace(/[^\w.\-]/g, '_')}`
    const up = await supabase.storage.from('personnel-evidence').upload(path, f.file)
    if (up.error) { setBusy(false); failed('Upload failed', up.error.message); return }
    const title = (f.title || '').trim() || f.file.name
    const payload = {
      auditor_id: auditorId, evidence_type: f.evidence_type, title,
      description: f.description || null, evidence_date: f.evidence_date || null, expiry_date: f.expiry_date || null,
      file_path: path, file_name: f.file.name, file_mime_type: f.file.type || null, file_size: f.file.size,
      uploaded_by: profile?.name || 'Staff',
    }
    const { error } = await supabase.from('personnel_evidence').insert(payload)
    setBusy(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction('Personnel evidence uploaded', 'personnel_evidence', auditorId, `${f.evidence_type}: ${title}`, { evidence_type: f.evidence_type })
    setForm(null); load()
  }

  async function open(r, download) {
    const opts = download ? { download: r.file_name || true } : undefined
    const { data, error } = await supabase.storage.from('personnel-evidence').createSignedUrl(r.file_path, 600, opts)
    if (error || !data?.signedUrl) { failed("Couldn't open the file", error?.message); return }
    window.open(data.signedUrl, '_blank', 'noopener')
  }
  async function remove(r) {
    if (!window.confirm('Delete this evidence file? This cannot be undone.')) return
    setBusy(true)
    await supabase.storage.from('personnel-evidence').remove([r.file_path])
    const { error } = await supabase.from('personnel_evidence').delete().eq('id', r.id)
    setBusy(false)
    if (error) { failed("Couldn't delete", error.message); return }
    if (logAction) logAction('Personnel evidence deleted', 'personnel_evidence', auditorId, `${r.evidence_type}: ${r.title || r.file_name}`, {})
    load()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Supporting competence evidence (certificates, training, WSHO/WSH cards, assessments). Stored privately.</div>
        {canApprove && !form && <button className="sm primary" onClick={() => setForm({ evidence_type: 'Qualification', evidence_date: new Date().toISOString().split('T')[0] })}>+ Add evidence</button>}
      </div>

      {form && (
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--bg2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <div className="field"><label>Evidence type</label>
              <select value={form.evidence_type || ''} onChange={e => setF('evidence_type', e.target.value)}>
                {EVIDENCE_TYPES.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div className="field"><label>Title</label><input value={form.title || ''} onChange={e => setF('title', e.target.value)} placeholder="(defaults to file name)" /></div>
            <div className="field"><label>Date recorded</label><input type="date" value={form.evidence_date || ''} onChange={e => setF('evidence_date', e.target.value)} /></div>
            <div className="field"><label>Expiry date (optional)</label><input type="date" value={form.expiry_date || ''} onChange={e => setF('expiry_date', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label>Description / remarks</label><textarea rows={2} value={form.description || ''} onChange={e => setF('description', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label>File</label><input type="file" onChange={e => setF('file', e.target.files[0] || null)} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="primary" onClick={uploadAndSave} disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button>
            <button onClick={() => setForm(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Type</th><th>Title</th><th>Date</th><th>Expiry</th><th>Uploaded by</th><th>Uploaded</th><th></th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 20 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 20 }}>No evidence files attached.</td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize: 13 }}>{r.evidence_type}</td>
                  <td style={{ fontSize: 13, maxWidth: 200 }}>{r.title || r.file_name || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.evidence_date)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.expiry_date)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{r.uploaded_by || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.uploaded_at)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="sm" onClick={() => open(r, false)}>View</button>{' '}
                    <button className="sm" onClick={() => open(r, true)}>Download</button>
                    {canApprove && <>{' '}<button className="sm danger" onClick={() => remove(r)} disabled={busy}>Delete</button></>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── TAEPP tracking (trainers) ────────────────────────────────────────────────────────────────────────────
// Pieces used by the Training page for trainers: the cycle totals (NAE / IAL CPD from the training ledger +
// practice hours), the practice-hours form and log, and the Export box. Rules live in lib/taepp.js.
const fmtDay = d => { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch (e) { return d || '—' } }

// Cycle label, or a picker when the trainer has past cycles. Shared by the TAEPP and Practice hours sections.
function TaeppCyclePicker({ cycles, ci, setCi, hasCurrent }) {
  const cycle = cycles[ci]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 13, color: 'var(--text2)' }}>
      <span>Cycle:</span>
      {cycles.length > 1 ? (
        <select value={ci} onChange={e => setCi(Number(e.target.value))} style={{ width: 'auto', fontSize: 13 }}>
          {cycles.map((c, i) => <option key={c.start} value={i}>{fmtDay(c.start)} – {fmtDay(c.end)}{i === 0 && hasCurrent ? ' (current)' : ''}</option>)}
        </select>
      ) : <strong style={{ color: 'var(--text)' }}>{fmtDay(cycle.start)} – {fmtDay(cycle.end)}</strong>}
    </div>
  )
}

function TaeppSummary({ t }) {
  const line = (label, done, req, short) => (
    <tr key={label}>
      <td>{label}</td>
      <td>{fmtHrs(done)}h</td>
      <td>{req}h</td>
      <td>{short > 0 ? <span style={{ color: '#854F0B' }}>{fmtHrs(short)}h</span> : <span style={{ color: '#27500a' }}>Met</span>}</td>
    </tr>
  )
  return (
    <div className="table-wrap"><table style={{ fontSize: 13 }}>
      <thead><tr><th></th><th>Done</th><th>Required</th><th>Shortfall</th></tr></thead>
      <tbody>
        {line('NAE Registry CPD (incl. IAL)', t.nae, TAEPP_REQ.nae, t.naeShort)}
        {line('of which IAL', t.ial, TAEPP_REQ.ial, t.ialShort)}
        {line('Practice hours', t.practice, TAEPP_REQ.practice, t.practiceShort)}
      </tbody>
    </table></div>
  )
}

function PracticeForm({ auditor, pe, setPe, current, onSaved }) {
  const [busy, setBusy] = useState(false)
  const setP = (k, v) => setPe(e => ({ ...e, [k]: v }))
  const outsideCurrent = current && pe.date_start &&
    (pe.date_start < current.start || (pe.date_end || pe.date_start) > current.end)

  async function save() {
    const missing = [['organisation', 'Organisation / client'], ['date_start', 'Date start'], ['hours', 'Hours'], ['role', 'Role'], ['area', 'Area']]
      .filter(([k]) => pe[k] === '' || pe[k] == null || String(pe[k]).trim() === '').map(([, l]) => l)
    if (missing.length) { toast('Fill in the missing fields', 'warning', { detail: missing.join(', ') }); return }
    setBusy(true)
    const row = {
      auditor_id: auditor.id,
      organisation: String(pe.organisation).trim(),
      programme: (pe.programme || '').trim() || null,
      date_start: pe.date_start,
      date_end: pe.date_end || null,
      hours: Number(pe.hours),
      role: pe.role,
      area: pe.area,
      remarks: pe.remarks || null,
      updated_at: new Date().toISOString(),
    }
    const res = pe.id
      ? await supabase.from('trainer_practice_hours').update(row).eq('id', pe.id)
      : await supabase.from('trainer_practice_hours').insert(row)
    setBusy(false)
    if (res.error) { failed("Couldn't save", res.error.message); return }
    setPe(null)
    onSaved()
  }

  return (
    <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div className="field"><label>Organisation / client *</label>
        <input value={pe.organisation || ''} onChange={e => setP('organisation', e.target.value)} /></div>
      <div className="field"><label>Course / programme</label>
        <input value={pe.programme || ''} onChange={e => setP('programme', e.target.value)} /></div>
      <div className="grid-2">
        <div className="field"><label>Date start *</label>
          <input type="date" value={pe.date_start || ''} onChange={e => setP('date_start', e.target.value)} /></div>
        <div className="field"><label>Date end</label>
          <input type="date" value={pe.date_end || ''} onChange={e => setP('date_end', e.target.value)} /></div>
      </div>
      {outsideCurrent && (
        <div style={{ fontSize: 13, color: '#854F0B', marginTop: -4, marginBottom: 8 }}>
          These dates fall outside {auditor.name}'s current cycle ({fmtDay(current.start)} – {fmtDay(current.end)}).
        </div>
      )}
      <div className="grid-2">
        <div className="field"><label>Hours *</label>
          <input type="number" step="0.5" value={pe.hours} onChange={e => setP('hours', e.target.value)} /></div>
        <div className="field"><label>Role *</label>
          <select value={pe.role || ''} onChange={e => { const v = e.target.value; setPe(p => ({ ...p, role: v, area: ROLE_AREA[v] || p.area })) }}>
            <option value="">Select…</option>
            {PRACTICE_ROLES.map(r => <option key={r}>{r}</option>)}
          </select></div>
        <div className="field"><label>Area *</label>
          <select value={pe.area || ''} onChange={e => setP('area', e.target.value)}>
            <option value="">Select…</option>
            {PRACTICE_AREAS.map(a => <option key={a}>{a}</option>)}
          </select></div>
        <div className="field"><label>Remarks</label>
          <input value={pe.remarks || ''} onChange={e => setP('remarks', e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button className="sm primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button className="sm" onClick={() => setPe(null)}>Cancel</button>
      </div>
    </div>
  )
}

function PracticeList({ rows, isOrphan, onEdit, onDelete }) {
  if (rows.length === 0) return <div style={{ fontSize: 13, color: 'var(--text3)' }}>No practice hours recorded in this cycle.</div>
  return (
    <div className="table-wrap"><table style={{ fontSize: 13 }}>
      <thead><tr><th style={{ width: 30 }}>#</th><th>Organisation / client</th><th>Course / programme</th><th style={{ width: 170 }}>Dates</th><th style={{ width: 60 }}>Hours</th><th>Role</th><th>Area</th><th style={{ width: 110 }}></th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id}>
            <td style={{ color: 'var(--text3)' }}>{i + 1}</td>
            <td>{r.organisation}{isOrphan(r) && <span className="badge badge-warn" style={{ marginLeft: 6, fontSize: 13 }}>outside cycle</span>}</td>
            <td>{r.programme || '—'}</td>
            <td>{fmtDay(r.date_start)}{r.date_end && r.date_end !== r.date_start ? ` – ${fmtDay(r.date_end)}` : ''}</td>
            <td>{fmtHrs(r.hours)}</td>
            <td>{r.role}</td>
            <td>{r.area}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              <button className="sm" onClick={() => onEdit(r)}>Edit</button>{' '}
              <button className="sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onDelete(r)}>Del</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table></div>
  )
}

// Export ▾ — one CSV (opens in Excel) with training records and/or practice hours. Training records can be
// limited to a period: all, a calendar year (by start date), or the selected TAEPP cycle (by finish date).
function ExportBox({ auditor, records, practice, cycle, cpdLabel }) {
  const [open, setOpen] = useState(false)
  const [incTraining, setIncTraining] = useState(true)
  const [incPractice, setIncPractice] = useState(!!auditor.is_trainer)
  const [period, setPeriod] = useState('all')
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const years = [...new Set(records.map(r => String(r.date_start || '').slice(0, 4)).filter(y => /^\d{4}$/.test(y)))].sort().reverse()
  const canPractice = !!auditor.is_trainer && !!cycle

  function download() {
    if (!incTraining && !incPractice) { toast('Tick at least one to export', 'warning'); return }
    const q = c => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`
    const out = []
    if (incTraining) {
      let list = records
      let label = 'All records'
      if (period.startsWith('year:')) { const y = period.slice(5); list = records.filter(r => String(r.date_start || '').startsWith(y)); label = y }
      if (period === 'cycle' && cycle) { list = records.filter(r => inTaeppCycle(r, cycle)); label = `TAEPP cycle ${fmtDay(cycle.start)} to ${fmtDay(cycle.end)}` }
      list = [...list].sort((a, b) => String(a.date_start || '').localeCompare(String(b.date_start || '')))
      out.push([`Training records — ${auditor.name}`], [`Period: ${label}`], [],
        ['S/N', 'Course title', 'Duration', 'Date start', 'Date end', 'Trainer / provider', 'CPD type', 'Points / hours', 'Remarks'],
        ...list.map((r, i) => [i + 1, r.training_content || '', r.duration_text || '', r.date_start ? fmtDay(r.date_start) : '',
          r.date_end ? fmtDay(r.date_end) : '', r.trainer || '', cpdLabel(r), r.hours != null ? fmtHrs(r.hours) : '', r.remarks || '']),
        [], [`Total points / hours: ${fmtHrs(list.reduce((s, r) => s + (Number(r.hours) || 0), 0))}`])
    }
    if (incPractice && canPractice) {
      const list = practice.filter(r => inTaeppCycle(r, cycle)).sort((a, b) => String(a.date_start).localeCompare(String(b.date_start)))
      if (out.length) out.push([], [])
      out.push([`TAEPP practice hours — ${auditor.name}`], [`Cycle: ${fmtDay(cycle.start)} to ${fmtDay(cycle.end)}`], [],
        ['S/N', 'AE full name', 'Organisation / client', 'Course / programme', 'Role', 'Area', 'Date start', 'Date end', 'Hours', 'Remarks'],
        ...list.map((r, i) => [i + 1, auditor.name, r.organisation || '', r.programme || '', r.role || '', r.area || '',
          fmtDay(r.date_start), r.date_end ? fmtDay(r.date_end) : '', fmtHrs(r.hours), r.remarks || '']),
        [], [`Total practice hours: ${fmtHrs(list.reduce((s, r) => s + (Number(r.hours) || 0), 0))}`])
    }
    const csv = '﻿' + out.map(row => row.map(q).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    const what = incTraining && incPractice && canPractice ? 'Training_and_Practice_Hours' : incTraining ? 'Training_Records' : 'Practice_Hours'
    a.href = url
    a.download = `${what}_${auditor.name.replace(/[^A-Za-z0-9]+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setOpen(false)
  }

  const chk = (checked, onChange, label, disabled) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0', cursor: disabled ? 'default' : 'pointer', color: disabled ? 'var(--text3)' : 'var(--text)' }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />{label}
    </label>
  )
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className="sm" onClick={() => setOpen(o => !o)}>Export ▾</button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 40, minWidth: 240, padding: 12,
          background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.14)' }}>
          {auditor.is_trainer && <>
            {chk(incTraining, () => setIncTraining(v => !v), 'Training records')}
            {chk(incPractice && canPractice, () => setIncPractice(v => !v), 'Practice hours', !canPractice)}
          </>}
          {incTraining && (
            <div className="field" style={{ margin: auditor.is_trainer ? '8px 0 0' : 0 }}><label>Training records period</label>
              <select value={period} onChange={e => setPeriod(e.target.value)}>
                <option value="all">All records</option>
                {years.map(y => <option key={y} value={`year:${y}`}>{y}</option>)}
                {auditor.is_trainer && cycle && <option value="cycle">TAEPP cycle ({fmtDay(cycle.start)} – {fmtDay(cycle.end)})</option>}
              </select></div>
          )}
          <button className="sm primary" style={{ marginTop: 10, width: '100%' }} onClick={download}>Download</button>
        </div>
      )}
    </span>
  )
}

// ── Training records (HRD-QR-02) ─────────────────────────────────────────────────────────────────────────
// One row per training event attended, mirroring the columns of the old HRD-QR-02 Word form so a record
// keyed here reads the same as the form it replaces (M&P 3.2 "Delivery and records"; Appendix C). This is
// also the ONLY source for CPD: cpd_type ('Accredited' = SDU-point courses / 'Non-accredited' = other
// courses) + hours + date_start feed the cycle-year calculation directly — there is no separate manually-
// Wrap a plain-text email body in the CCIS branded shell (same teal header/footer as the client renewal
// emails) so internal notifications look consistent instead of raw text. Bare URLs become clickable links.
function brandedEmailHtml(bodyText) {
  const esc = s => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const inner = esc(bodyText)
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color:#006363;">$1</a>')
    .replace(/\n/g, '<br>')
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head>
<body bgcolor="#f4f5f3" style="margin:0;padding:0;background:#f4f5f3;font-family:Arial,Helvetica,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f3;"><tr><td align="center" style="padding:28px 12px;">
  <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;border:1px solid #e3e5e2;">
    <tr><td bgcolor="#006363" style="background:#006363;padding:18px 32px;">
      <div style="font-size:14px;font-weight:700;color:#ffffff;letter-spacing:1.5px;">CCIS SINGAPORE PTE LTD</div>
      <div style="font-size:10px;color:#a9d6d6;letter-spacing:1px;margin-top:3px;">FOR A BRIGHTER BUSINESS TOMORROW</div>
    </td></tr>
    <tr><td style="padding:26px 32px;color:#2b2b2b;font-size:14px;line-height:1.7;">${inner}</td></tr>
    <tr><td style="padding:14px 32px 20px;border-top:1px solid #ececea;">
      <div style="font-size:11px;color:#9a9a9a;line-height:1.55;">${esc(FIRM_NAME())} &middot; Reg. No. 199205611D<br>16 Jalan Kilang Timor #08-03, Redhill Forum Singapore 159308</div>
      <div style="font-size:11px;color:#b0b0b0;line-height:1.55;margin-top:10px;">This is an automated message from AuditPM.</div>
    </td></tr>
  </table>
</td></tr></table>
</body></html>`
}

// typed CPD screen. Saving a NEW record auto-emails the auditor a link to the external Training Evaluation
// form; the trainee's own evaluation is no longer collected here (they fill the form instead).
function TrainingModal({ auditor, cycleStart, onClose }) {
  const [records, setRecords] = useState([])
  const [practice, setPractice] = useState([])     // TAEPP practice-hour entries (trainers only)
  const [practiceError, setPracticeError] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)   // record object being added/edited, or null
  const [busy, setBusy] = useState(false)
  // Which section is unfolded (one at a time): 'sac' | 'taepp' | 'training' | 'practice' | null
  const [open, setOpen] = useState(null)
  const [pe, setPe] = useState(null)             // practice-hours entry being added/edited, or null
  const [ci, setCi] = useState(0)                // selected TAEPP cycle (0 = current), shared by TAEPP + Practice
  const toggle = s => setOpen(o => o === s ? null : s)

  useEffect(() => { load() }, [auditor.id])   // eslint-disable-line
  async function load() {
    const { data, error } = await supabase.from('auditor_training').select('*')
      .eq('auditor_id', auditor.id)
      .order('date_start', { ascending: false, nullsFirst: false })
      .order('sort_order', { ascending: true })
    if (error) failed("Couldn't load training records", error.message)
    setRecords(data || [])
    if (auditor.is_trainer) {
      const { data: ph, error: phErr } = await supabase.from('trainer_practice_hours').select('*')
        .eq('auditor_id', auditor.id).order('date_start', { ascending: false })
      setPractice(ph || [])
      setPracticeError(phErr ? phErr.message : '')
    }
    setLoading(false)
  }

  const blank = { training_content: '', duration_text: '', hours: '', date_start: '', date_end: '', trainer: '', cpd_type: '', remarks: '', head_evaluation: '', head_evaluated_on: '' }
  const setF = (k, v) => setEditing(e => ({ ...e, [k]: v }))
  const fmtD = d => { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch (e) { return d || '—' } }

  // ONE "CPD type" dropdown. The two TAEPP options are stored as cpd_type 'Non-accredited' + taepp_type, so a
  // TAEPP course always counts as SAC non-accredited and the SAC calculation is untouched.
  const cpdValue = r => r.taepp_type ? `TAEPP_${r.taepp_type}` : (r.cpd_type || '')
  const cpdLabel = r => r.taepp_type ? (TAEPP_LABEL[r.taepp_type] || r.taepp_type) : (r.cpd_type || 'Not classified')
  function setCpd(v) {
    if (v.startsWith('TAEPP_')) setEditing(e => ({ ...e, cpd_type: 'Non-accredited', taepp_type: v.slice(6) }))
    else setEditing(e => ({ ...e, cpd_type: v, taepp_type: null }))
  }

  // Best-effort, non-blocking — same pattern as notifyAuditorsOfReturn in JobDetail.js.
  async function sendEvalEmail(rec) {
    if (!auditor.email) return
    const tmpl = applyTemplate(getTemplate('trainingEvaluation'), {
      AUDITOR_NAME: auditor.name || '', TRAINING_CONTENT: rec.training_content || '',
      TRAINING_DATE: rec.date_start ? fmtD(rec.date_start) : '', EVAL_LINK: TRAINING_EVAL_FORM_URL, FIRM_NAME: FIRM_NAME(),
    })
    try {
      await supabase.functions.invoke('send-internal-email', { body: { to: auditor.email, subject: tmpl.subject, html: brandedEmailHtml(tmpl.body) } })
      await supabase.from('auditor_training').update({ eval_email_sent_at: new Date().toISOString() }).eq('id', rec.id)
    } catch (e) { /* best-effort — non-blocking */ }
  }

  async function save() {
    if (!(editing.training_content || '').trim()) { toast('Enter the course title', 'warning'); return }
    if (!editing.id && !editing.cpd_type) { toast('Choose a CPD type', 'warning'); return }
    setBusy(true)
    const isNew = !editing.id
    const row = {
      auditor_id: auditor.id,
      training_content: editing.training_content.trim(),
      duration_text: editing.duration_text || null,
      hours: editing.hours === '' || editing.hours == null ? null : Number(editing.hours),
      date_start: editing.date_start || null,
      date_end: editing.date_end || null,
      trainer: editing.trainer || null,
      cpd_type: editing.cpd_type || null,
      remarks: editing.remarks || null,
      // self_evaluation is no longer collected via this form (the auditor fills the external Evaluation form
      // instead) — but if an older record already has one, carry it through unchanged rather than wiping it.
      self_evaluation: editing.self_evaluation || null,
      head_evaluation: editing.head_evaluation || null,
      head_evaluated_on: editing.head_evaluated_on || null,
      updated_at: new Date().toISOString(),
    }
    // Only send taepp_type when it applies (a trainer, or a record already carrying the column).
    if (auditor.is_trainer || 'taepp_type' in editing) row.taepp_type = editing.taepp_type || null
    const res = isNew
      ? await supabase.from('auditor_training').insert(row).select().maybeSingle()
      : await supabase.from('auditor_training').update(row).eq('id', editing.id)
    setBusy(false)
    if (res.error) { failed("Couldn't save", res.error.message); return }
    setEditing(null)
    setOpen('training')                          // show the list so the new/edited record is visible
    if (isNew && res.data) sendEvalEmail(res.data)
    load()
  }
  async function removePractice(r) {
    if (!window.confirm('Delete this practice-hours entry? This cannot be undone.')) return
    const { error } = await supabase.from('trainer_practice_hours').delete().eq('id', r.id)
    if (error) { failed("Couldn't delete", error.message); return }
    load()
  }
  async function remove(rec) {
    if (!window.confirm('Delete this training record? This cannot be undone.')) return
    const { error } = await supabase.from('auditor_training').delete().eq('id', rec.id)
    if (error) { failed("Couldn't delete", error.message); return }
    load()
  }
  async function resendEval(rec) {
    if (!auditor.email) { toast(`No email for ${auditor.name}`, 'warning', { detail: 'Add one to send the evaluation link' }); return }
    if (!window.confirm(`Resend the Training Evaluation form link to ${auditor.name}?`)) return
    await sendEvalEmail(rec)
    load()
  }

  // Cycle-year CPD summary — only meaningful for non-WSHO auditors, and only once a cycle start date is set
  // under Settings → AO Scheme accreditation. Everyone else just sees the plain record list below.
  const wshoValid = isWshoValid(auditor)
  const showCycle = auditor.is_auditor && !wshoValid && !!cycleStart
  const cycle = showCycle ? computeCpdCycle(records, cycleStart) : null

  // Fallback view (no applicable cycle): hours completed per calendar year.
  const byYear = {}
  for (const r of records) {
    const y = r.date_start ? String(r.date_start).slice(0, 4) : '—'
    byYear[y] = (byYear[y] || 0) + (Number(r.hours) || 0)
  }
  const years = Object.keys(byYear).sort().reverse()

  // TAEPP (trainers only): selected cycle + its totals; practice entries shown for that cycle. On the current
  // cycle, entries inside NO known cycle are shown too (flagged) so a mistyped date never makes one vanish.
  const isTrainer = !!auditor.is_trainer
  const tCycles = isTrainer ? allTaeppCycles(auditor) : []
  const tCycle = tCycles[ci] || null
  const tCurrent = currentTaeppCycle(auditor)
  const taepp = tCycle ? computeTaepp(records, practice, tCycle) : null
  const isOrphan = r => !tCycles.some(c => inTaeppCycle(r, c))
  const practiceRows = tCycle ? practice.filter(r => inTaeppCycle(r, tCycle) || (ci === 0 && isOrphan(r))) : practice
  const noCycleNote = (
    <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 13, color: 'var(--text2)' }}>
      No TAEPP cycle set yet — add the cycle start date on {auditor.name}'s Profile tab (View → Edit).
    </div>
  )
  const foldBtn = (key, label) => (
    <button className={open === key ? 'sm primary' : 'sm'} onClick={() => toggle(key)}>{label} {open === key ? '▴' : '▾'}</button>
  )

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Training records — {auditor.name}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Every training event attended (replaces form HRD-QR-02 — and is now also the single source for CPD).
        </p>

        {/* Fold buttons — one section open at a time; everything starts folded. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {showCycle && foldBtn('sac', 'SAC CPD')}
          {isTrainer && foldBtn('taepp', 'TAEPP')}
          {foldBtn('training', 'Training records')}
          {isTrainer && foldBtn('practice', 'Practice hours')}
        </div>

        {open === 'taepp' && isTrainer && (
          <div style={{ marginBottom: 14 }}>
            {tCycle ? <>
              <TaeppCyclePicker cycles={tCycles} ci={ci} setCi={setCi} hasCurrent={!!tCurrent} />
              <TaeppSummary t={taepp} />
            </> : noCycleNote}
          </div>
        )}

        {open === 'practice' && isTrainer && (
          <div style={{ marginBottom: 14 }}>
            {practiceError
              ? <div style={{ fontSize: 13, color: 'var(--danger)' }}>Could not load practice hours: {practiceError} (has sql/TAEPP_CPD.sql been run?)</div>
              : <>
                  {tCycle ? <TaeppCyclePicker cycles={tCycles} ci={ci} setCi={setCi} hasCurrent={!!tCurrent} /> : noCycleNote}
                  <PracticeList rows={practiceRows} isOrphan={isOrphan} onEdit={r => { setEditing(null); setPe({ ...r, hours: r.hours ?? '' }) }} onDelete={removePractice} />
                </>}
          </div>
        )}

        {showCycle ? open === 'sac' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
              CPD cycle (SAC AO Scheme accreditation) — 10 Accredited + 10 Non-accredited SDU points/hours per
              year (1 SDU = 1 hour), measured <strong>cumulatively</strong> (by the end of year N: {'≥'} 10×N of
              each). Excess accredited hours carry forward across the whole cycle and may substitute for the
              non-accredited requirement (never the reverse). Compliance is assessed against the latest completed
              cycle-year, so an earlier shortfall that has since been made up is not held against the auditor;
              nothing carries into the next cycle.
            </div>
            <div className="table-wrap"><table style={{ fontSize: 13 }}>
              <thead><tr>
                <th>Cycle year</th>
                <th>Accredited (cum / req)</th>
                <th>Non-accredited (cum / req)</th>
                <th>Accredited surplus used</th>
                <th>Shortfall</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {cycle.years.map(y => {
                  const fmtH = n => n.toFixed(1).replace(/\.0$/, '')
                  // "Below (made up later)": this closed year was short on its own checkpoint, but the auditor is
                  // compliant at the latest binding checkpoint — i.e. it was subsequently recovered.
                  const recovered = y.elapsed && !y.met && cycle.sufficient
                  return (
                  <tr key={y.index}>
                    <td>{format(y.start, 'd MMM yyyy')} – {format(addDays(y.end, -1), 'd MMM yyyy')}{y.current ? ' (current)' : ''}</td>
                    <td>{fmtH(y.accCum)} / {y.reqCum}h</td>
                    <td>{fmtH(y.nonCum)} / {y.reqCum}h</td>
                    <td>{y.offsetUsed > 0 ? <span style={{ color: '#0e6b6b' }}>{fmtH(y.offsetUsed)}h → non-accred</span> : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td>{(y.remainingAcc > 0 || y.remainingNon > 0)
                          ? <span style={{ color: (y.elapsed && !recovered) ? '#791F1F' : '#854F0B' }}>
                              {[y.remainingAcc > 0 ? `${fmtH(y.remainingAcc)}h acc` : null,
                                y.remainingNon > 0 ? `${fmtH(y.remainingNon)}h non` : null].filter(Boolean).join(' · ')}
                            </span>
                          : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td>
                      {!y.elapsed && !y.current ? <span style={{ color: 'var(--text3)' }}>Upcoming</span>
                        : y.met ? <span style={{ color: '#27500a' }}>Met</span>
                        : recovered ? <span style={{ color: '#854F0B' }}>Below (made up later)</span>
                        : y.elapsed ? <span style={{ color: '#791F1F', fontWeight: 500 }}>Deficient</span>
                        : <span style={{ color: '#854F0B' }}>In progress</span>}
                    </td>
                  </tr>
                  )
                })}
                {(() => {
                  const fmtH = n => n.toFixed(1).replace(/\.0$/, '')
                  const last = cycle.years[cycle.years.length - 1]
                  return (
                    <tr style={{ borderTop: '1.5px solid var(--border)', fontWeight: 600 }}>
                      <td>Total (whole cycle)</td>
                      <td>{fmtH(last.accCum)}h</td>
                      <td>{fmtH(last.nonCum)}h</td>
                      <td colSpan={3}></td>
                    </tr>
                  )
                })()}
              </tbody>
            </table></div>
          </div>
        ) : !isTrainer && years.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {years.map(y => (
              <span key={y} className="badge badge-info">{y}: {byYear[y] ? byYear[y].toFixed(1).replace(/\.0$/, '') : '0'} hrs · {records.filter(r => (r.date_start ? String(r.date_start).slice(0, 4) : '—') === y).length} record(s)</span>
            ))}
          </div>
        )}

        {/* Actions — always visible; forms open right below this row. */}
        {!editing && !pe && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <button className="sm primary" onClick={() => setEditing({ ...blank })}>+ Add training record</button>
            {isTrainer && !practiceError && <button className="sm primary" onClick={() => setPe({ organisation: '', programme: '', date_start: '', date_end: '', hours: '', role: '', area: '', remarks: '' })}>+ Add practice hours</button>}
            <ExportBox auditor={auditor} records={records} practice={practice} cycle={tCycle} cpdLabel={cpdLabel} />
          </div>
        )}

        {pe && <PracticeForm auditor={auditor} pe={pe} setPe={setPe} current={tCurrent} onSaved={() => { setOpen('practice'); load() }} />}

        {editing && (
          <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div className="field"><label>Course Title *</label>
              <textarea rows={2} value={editing.training_content} onChange={e => setF('training_content', e.target.value)} placeholder="Course / topic covered" /></div>
            <div className="grid-2">
              <div className="field"><label>Duration (as written)</label>
                <input value={editing.duration_text} onChange={e => setF('duration_text', e.target.value)} placeholder="e.g. 1.5 hrs / 2 days" /></div>
              <div className="field"><label>CPD type</label>
                <select value={cpdValue(editing)} onChange={e => setCpd(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="Accredited">Accredited (SDU)</option>
                  <option value="Non-accredited">Non-accredited</option>
                  {(auditor.is_trainer || editing.taepp_type) && <>
                    <option value="TAEPP_NAE">{TAEPP_LABEL.NAE}</option>
                    <option value="TAEPP_IAL">{TAEPP_LABEL.IAL}</option>
                  </>}
                </select>
                {editing.taepp_type && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 3 }}>Also counts as SAC non-accredited.</div>}</div>
              <div className="field"><label>Date start</label>
                <input type="date" value={editing.date_start || ''} onChange={e => setF('date_start', e.target.value)} /></div>
              <div className="field"><label>Date end</label>
                <input type="date" value={editing.date_end || ''} onChange={e => setF('date_end', e.target.value)} /></div>
              <div className="field"><label>Trainer / provider</label>
                <input value={editing.trainer} onChange={e => setF('trainer', e.target.value)} /></div>
              <div className="field"><label>Points / hours</label>
                <input type="number" step="0.5" value={editing.hours} onChange={e => setF('hours', e.target.value)} placeholder="e.g. 16" /></div>
            </div>
            <div className="field"><label>Remarks (if any)</label>
              <input value={editing.remarks} onChange={e => setF('remarks', e.target.value)} /></div>
            <div className="grid-2">
              <div className="field"><label>Functional Head's evaluation (on-the-job, ~2 months later)</label>
                <textarea rows={2} value={editing.head_evaluation} onChange={e => setF('head_evaluation', e.target.value)} /></div>
              <div className="field"><label>Head evaluated on</label>
                <input type="date" value={editing.head_evaluated_on || ''} onChange={e => setF('head_evaluated_on', e.target.value)} /></div>
            </div>
            {!editing.id && (
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2, marginBottom: 6 }}>
                Saving will email {auditor.name} the Training Evaluation form to complete{auditor.email ? '' : ' — but they have no email on record, so it will not be sent'}.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button className="sm primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save record'}</button>
              <button className="sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}

        {open !== 'training' ? (loading && <div style={{ color: 'var(--text3)' }}>Loading…</div>)
        : records.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No training records yet.</div>
        ) : (
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width: 34 }}>#</th><th>Training content</th><th style={{ width: 90 }}>Duration</th><th style={{ width: 110 }}>Start</th><th style={{ width: 110 }}>End</th><th style={{ width: 150 }}>Trainer</th><th style={{ width: 130 }}>CPD type</th><th style={{ width: 160 }}></th></tr></thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id}>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>{i + 1}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.training_content}
                    {(r.self_evaluation || r.head_evaluation) && <span className="badge badge-ok" style={{ marginLeft: 6 }}>evaluated</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{r.duration_text || (r.hours != null ? r.hours + ' hrs' : '—')}</td>
                  <td style={{ fontSize: 13 }}>{fmtD(r.date_start)}</td>
                  <td style={{ fontSize: 13 }}>{fmtD(r.date_end)}</td>
                  <td style={{ fontSize: 13 }}>{r.trainer || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{cpdLabel(r)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="sm" onClick={() => { setPe(null); setEditing({ ...r, hours: r.hours ?? '' }) }}>Edit</button>{' '}
                    <button className="sm" title="Resend the Training Evaluation form link" onClick={() => resendEval(r)}>Resend</button>{' '}
                    <button className="sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => remove(r)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  )
}

// RM & SHMS activity breakdown for the audit-log modal — answers the standing SAC questions per auditor:
// how many RM / ConSASS audits and how many on-site days, split by Lead vs Auditor role, over the cycle
// OR any date window the assessor names. ConSASS is nested under SHMS (a ConSASS-tagged row IS an SHMS row —
// ConSASS is the Construction Worksite audit criteria). Figures come from AuditPM-recorded external audits;
// the QM-attested baseline lump and ISO/other-scheme audits are not itemised here.
function AuditBreakdown({ logs, cycleStart, cycleEnd }) {
  const toISO = d => d ? format(d, 'yyyy-MM-dd') : ''
  const cycFrom = toISO(cycleStart)
  const cycTo = cycleEnd ? toISO(addDays(cycleEnd, -1)) : ''
  const [from, setFrom] = useState(cycFrom)
  const [to, setTo] = useState(cycTo)
  useEffect(() => { setFrom(cycFrom); setTo(cycTo) }, [cycFrom, cycTo])   // follow Settings cycle changes

  const inWindow = (logs || []).filter(l =>
    l.is_external && l.audit_date && l.scheme !== 'REVIEW' &&
    (!from || l.audit_date >= from) && (!to || l.audit_date <= to))

  const agg = (pred) => {
    const rs = inWindow.filter(pred)
    const days = arr => Math.round(arr.reduce((s, l) => s + (parseFloat(l.mandays) || 0), 0) * 10) / 10
    const lead = rs.filter(l => l.role === 'Lead Auditor')
    const asAud = rs.filter(l => l.role !== 'Lead Auditor')
    return { audits: rs.length, lead: lead.length, auditor: asAud.length,
      leadDays: days(lead), auditorDays: days(asAud), totalDays: days(rs) }
  }
  const rm = agg(l => l.scheme === 'bizSAFE RM')
  const shms = agg(l => l.scheme === 'SHMS' || l.scheme === 'ConSASS')
  const consass = agg(l => l.scheme === 'ConSASS')
  const total = agg(l => l.scheme === 'bizSAFE RM' || l.scheme === 'SHMS' || l.scheme === 'ConSASS')

  const th = { padding: '4px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--text2)', fontSize: 13, whiteSpace: 'nowrap' }
  const cR = { padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
  const row = (label, d, opts = {}) => {
    const bt = opts.rule ? '1px solid var(--brand)' : undefined
    return (
      <tr style={{ fontWeight: opts.bold ? 600 : 400 }}>
        <td style={{ padding: '5px 8px', paddingLeft: opts.indent ? 22 : 8, color: opts.sub ? 'var(--text2)' : undefined, borderTop: bt, whiteSpace: 'nowrap' }}>{label}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.audits}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.lead}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.auditor}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.leadDays}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.auditorDays}</td>
        <td style={{ ...cR, borderTop: bt }}>{d.totalDays}</td>
      </tr>
    )
  }
  const isCycle = from === cycFrom && to === cycTo
  return (
    <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 500, color: 'var(--brand-dark)' }}>RM &amp; SHMS breakdown <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text3)' }}>· AuditPM records</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text3)' }}>From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ fontSize: 13, padding: '2px 4px', width: 'auto' }} />
          <span style={{ color: 'var(--text3)' }}>To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ fontSize: 13, padding: '2px 4px', width: 'auto' }} />
          {!isCycle && cycFrom && <button className="sm" onClick={() => { setFrom(cycFrom); setTo(cycTo) }} style={{ fontSize: 13 }}>Whole cycle</button>}
        </div>
      </div>
      <div className="table-wrap"><table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={{ ...th, textAlign: 'left' }} />
          <th style={th}>Audits</th><th style={th}>as Lead</th><th style={th}>as Auditor</th>
          <th style={th}>Lead days</th><th style={th}>Auditor days</th><th style={th}>Total days</th>
        </tr></thead>
        <tbody>
          {row('RM Audit (bizSAFE RM)', rm)}
          {row('SHMS', shms)}
          {row('of which ConSASS', consass, { indent: true, sub: true })}
          {row('Total', total, { bold: true, rule: true })}
        </tbody>
      </table></div>
      <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>
        {isCycle ? 'Covers the current accreditation cycle.' : `Covers ${from || '…'} to ${to || '…'}.`} SHMS includes ConSASS rows (ConSASS is the Construction Worksite audit criteria under SHMS). Figures are AuditPM-recorded external audits only — the QM-attested baseline lump and ISO / other-scheme audits are not itemised here.
      </div>
    </div>
  )
}

function AuditLogModal({ auditor, cpdCycleStart, onClose }) {
  const navigate = useNavigate()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showOther, setShowOther] = useState(false)
  const [form, setForm] = useState({ audit_type: 'bizSAFE Risk Management Audit', audit_date: '', mandays: '', role: auditor.grade || 'Auditor', is_external: true, notes: '' })
  // Accreditation-cycle window (from Settings) so the maintenance figures here match the roster exactly.
  const cyc = { years: cpdCycleYears(cpdCycleStart) }

  useEffect(() => { load() }, [auditor.id])

  async function load() {
    // Paginate past PostgREST's 1000-row default cap — a backfilled career history easily exceeds it.
    const rows = []
    for (let from = 0; ; from += 1000) {
      const { data } = await supabase.from('auditor_audit_log').select('*')
        .eq('auditor_id', auditor.id).order('audit_date', { ascending: false }).range(from, from + 999)
      rows.push(...(data || []))
      if (!data || data.length < 1000) break
    }
    // Join the originating job → client name, quote ref, and signed-quote token (one extra query).
    const jobIds = [...new Set(rows.map(r => r.job_id).filter(Boolean))]
    const jobsById = {}
    if (jobIds.length) {
      const { data: jobs } = await supabase.from('jobs')
        .select('id, quote_ref, signature_token, signed_date, auditor_closed, combined_group_id, is_combined_primary, clients(name)').in('id', jobIds)
      ;(jobs || []).forEach(j => { jobsById[j.id] = j })
    }
    setLogs(rows.map(r => {
      const job = r.job_id ? jobsById[r.job_id] : null
      return {
        ...r,
        _client: job?.clients?.name || null,
        _quoteRef: job?.quote_ref || null,
        _jobId: job?.id || null,
        _signedToken: (job?.signed_date && job?.signature_token) ? job.signature_token : null,
        _combinedLinked: !!(job && job.combined_group_id && !job.is_combined_primary),
        _combinedPrimary: !!(job && job.combined_group_id && job.is_combined_primary),
      }
    }))
    setLoading(false)
  }

  async function add() {
    if (!form.audit_date || !form.mandays) { toast('Enter the date and man-days', 'warning'); return }
    await supabase.from('auditor_audit_log').insert({ ...form, auditor_id: auditor.id, mandays: parseFloat(form.mandays) })
    setShowAdd(false)
    setForm({ audit_type: 'bizSAFE Risk Management Audit', audit_date: '', mandays: '', role: auditor.grade || 'Auditor', is_external: true, notes: '' })
    load()
  }

  // The main table lists EXACTLY the rows the maintenance summary (and eligibility) counts — same rule as
  // maintenanceStatus / computeRecordsMaintenance: external, dated inside the accreditation cycle, and (baseline
  // mode) after an in-cycle baseline's as-of date, or (records mode) not awaiting classification.
  const cycStartD = cyc.years[0]?.start || null
  const cycEndD = cyc.years.length ? cyc.years[cyc.years.length - 1].end : null
  const recordsMode = auditor.maintenance_source === 'records'
  const asOf = auditor.baseline_as_of_date ? parseISO(auditor.baseline_as_of_date) : null
  const baselineInCycle = !!(asOf && cycStartD && cycEndD && asOf >= cycStartD && asOf < cycEndD)
  const whyNotCounted = l => {
    if (!l.is_external) return 'Internal audit (not external)'
    if (!l.audit_date) return 'No audit date'
    if (!cycStartD || !cycEndD) return 'No accreditation cycle set (Settings)'
    const d = parseISO(l.audit_date)
    if (d < cycStartD || d >= cycEndD) return 'Outside the current accreditation cycle'
    if (recordsMode && l.scheme === 'REVIEW') return 'Needs classification (REVIEW)'
    if (!recordsMode && baselineInCycle && !(d > asOf)) return 'Already included in the QM-attested baseline'
    return null
  }
  const countable = logs.filter(l => !whyNotCounted(l))
  const nonCounting = logs.filter(l => !!whyNotCounted(l))
  const reasonFor = l => whyNotCounted(l) || 'Excluded'
  // "Lead Auditor" rows that are not credited because the roster grade is only Auditor.
  const leadMismatch = auditor.grade === 'Lead Auditor' ? 0 : countable.filter(l => l.role === 'Lead Auditor').length

  const cell = { padding: '7px 8px', whiteSpace: 'nowrap' }
  function logRow(l, withReason) {
    const roleMismatch = l.role === 'Lead Auditor' && auditor.grade !== 'Lead Auditor'
    return (
      <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
        <td style={cell}>{l.audit_date ? format(parseISO(l.audit_date), 'd MMM yyyy') : '—'}</td>
        <td style={{ padding: '7px 8px' }}>{l._client || <span style={{ color: 'var(--text3)' }} title={l.notes || ''}>Manual entry</span>}</td>
        <td style={{ padding: '7px 8px', color: 'var(--text2)' }}>{(resolveScope(l.audit_type)?.name) || l.audit_type}</td>
        <td style={{ ...cell, color: roleMismatch ? '#791F1F' : undefined }}>
          {l.role}{roleMismatch && <span title={`Roster grade is ${auditor.grade || 'Auditor'} — not credited`}> ⚠</span>}
        </td>
        <td style={{ padding: '7px 8px', fontWeight: 500 }}>
          {l._combinedLinked
            ? <span style={{ fontWeight: 400, color: 'var(--text3)' }} title="Combined audit — audited in the same session; the man-day is recorded on the primary company">Combined</span>
            : l._combinedPrimary
              ? <span title="Combined audit — this row carries the combined man-day for all companies audited in the session">{l.mandays} <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 13 }}>· combined</span></span>
              : l.mandays}
        </td>
        <td style={{ padding: '7px 8px' }}>{l.is_external ? <span style={{ color: '#27500a' }}>✓</span> : <span style={{ color: '#888' }}>—</span>}</td>
        <td style={cell}>{l._quoteRef || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
        {withReason && <td style={{ ...cell, color: 'var(--text3)' }}>{reasonFor(l)}</td>}
        <td style={cell}>
          {l._jobId && <button className="sm" style={{ fontSize: 13, padding: '1px 6px' }} onClick={() => { onClose(); navigate(`/jobs/${l._jobId}`) }}>View job →</button>}
          {/* TEMPORARY: falls back to the public signing page. Replace with an internal read-only
              quotation view once one exists. */}
          {l._signedToken && <a href={`/sign/${l._signedToken}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, marginLeft: 6 }}>Signed quote ↗</a>}
        </td>
      </tr>
    )
  }
  const headStyle = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', fontWeight: 500, whiteSpace: 'nowrap' }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 940 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit log — {auditor.name}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>

        {/* 4-year maintenance summary — Baseline + AuditPM records = Effective total.
            The tiers reconcile the roster (effective) with the table below (AuditPM records only), so a high
            effective total sitting above an empty table is explained by the QM-attested baseline. */}
        {(() => {
          // Records-mode auditors (Phase D) show TRACK-SPECIFIC maintenance (RM / SHMS), not the baseline
          // reconciliation — each authorised track has its own family thresholds + 75%.
          if (auditor.maintenance_source === 'records') {
            const ms = computeRecordsMaintenance(auditor, logs, cyc)
            const gradeIsLead = auditor.grade === 'Lead Auditor'
            const th = { padding: '4px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--text2)', fontSize: 13 }
            const cellR = { padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
            const stateChip = s => {
              const map = { compliant: ['#27500a', '#EAF3DE', 'Compliant'], lapsed: ['#791F1F', '#FDECEA', 'Lapsed'], building: ['var(--text2)', 'var(--bg2)', 'Building'], basis_unset: ['#854F0B', '#FFF3E0', 'Set basis'] }
              const [c, b, t] = map[s] || map.basis_unset
              return <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 10, color: c, background: b }}>{t}</span>
            }
            const trackRow = (key, tr) => (
              <tr key={key}>
                <td style={{ padding: '5px 8px' }}>{key === 'RM' ? 'bizSAFE RM' : 'SHMS'} {stateChip(tr.state)}</td>
                <td style={{ ...cellR, color: tr.limbs.audits ? undefined : '#791F1F' }}>{tr.audits}</td>
                <td style={{ ...cellR, color: tr.limbs.days ? undefined : '#791F1F' }}>{tr.days}</td>
                {gradeIsLead && <td style={{ ...cellR, color: tr.limbs.lead ? undefined : '#791F1F' }}>{tr.leadAudits}</td>}
                <td style={{ ...cellR, color: tr.limbs.pct75 ? undefined : '#791F1F' }}>{tr.pct75val !== null ? `${tr.pct75val}%` : '—'}</td>
              </tr>
            )
            const keys = Object.keys(ms.tracks)
            return (
              <div style={{ background: 'var(--brand-light)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
                <div style={{ fontWeight: 500, marginBottom: 8, color: 'var(--brand-dark)' }}>4-year maintenance — CT 17 (accreditation cycle) · records mode</div>
                {keys.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No authorised maintenance track (no bizSAFE / SHMS approved scope).</div>
                  : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...th, textAlign: 'left' }}>Track</th>
                          <th style={th} title="Family audits (≥4)">Audits</th>
                          <th style={th} title="Family on-site days (≥10)">Days</th>
                          {gradeIsLead && <th style={th} title="Family audits as Lead (≥2)">As Lead</th>}
                          <th style={th} title="Family ÷ all countable external (≥75%)">75%</th>
                        </tr>
                      </thead>
                      <tbody>{keys.map(k => trackRow(k, ms.tracks[k]))}</tbody>
                    </table>
                  )}
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>
                  RM family = bizSAFE RM + all SHMS rows · SHMS family = SHMS rows only (a row tagged ConSASS is an SHMS row — ConSASS is the Construction Worksite audit criteria). Thresholds count family rows only; the 75% denominator is all countable external audits in the accreditation cycle window (REVIEW excluded).
                </div>
                {ms.reasons.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 13, color: '#791F1F', background: '#FDECEA', borderRadius: 6, padding: '6px 10px' }}>
                    {ms.reasons.map((r, i) => <div key={i}>⚠ {r}</div>)}
                  </div>
                )}
              </div>
            )
          }
          const eff = maintenanceStatus(auditor, logs, cyc)   // same effective totals the roster shows, windowed to the accreditation cycle
          const inCyc = eff.baselineInCycle
          const baseAudits = inCyc ? (parseInt(auditor.baseline_audits) || 0) : 0
          const baseDays = inCyc ? Math.round((parseFloat(auditor.baseline_auditor_days) || 0) * 10) / 10 : 0
          const baseLead = inCyc ? (parseInt(auditor.baseline_lead_audits) || 0) : 0
          const pmAudits = eff.effAudits - baseAudits            // AuditPM records = effective − baseline (never double-counted)
          const pmDays = Math.round((eff.effDays - baseDays) * 10) / 10
          const pmLead = eff.effLeadClaimed - baseLead
          const gradeIsLead = auditor.grade === 'Lead Auditor'
          const asOfLabel = auditor.baseline_as_of_date ? format(parseISO(auditor.baseline_as_of_date), 'd MMM yyyy') : null
          const num = v => Math.round(v * 10) / 10
          const th = { padding: '4px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--text2)', fontSize: 13 }
          const cellR = { padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
          const tier = (label, sub, a, d, l, opts = {}) => (
            <tr style={{ fontWeight: opts.bold ? 600 : 400 }}>
              <td style={{ padding: '5px 8px', borderTop: opts.rule ? '1px solid var(--brand)' : undefined }}>
                {label}{sub && <span style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 400 }}> · {sub}</span>}
              </td>
              <td style={{ ...cellR, borderTop: opts.rule ? '1px solid var(--brand)' : undefined }}>{num(a)}</td>
              <td style={{ ...cellR, borderTop: opts.rule ? '1px solid var(--brand)' : undefined }}>{num(d)}</td>
              {gradeIsLead && <td style={{ ...cellR, borderTop: opts.rule ? '1px solid var(--brand)' : undefined }}>{num(l)}</td>}
            </tr>
          )
          return (
            <div style={{ background: 'var(--brand-light)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: 'var(--brand-dark)' }}>4-year maintenance — CT 17 (accreditation cycle)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }} />
                    <th style={th}>Audits</th>
                    <th style={th}>On-site days</th>
                    {gradeIsLead && <th style={th}>As Lead</th>}
                  </tr>
                </thead>
                <tbody>
                  {tier('Baseline', inCyc ? `QM-attested, as of ${asOfLabel}` : (asOfLabel ? `attested ${asOfLabel} — outside this cycle` : 'none recorded'), baseAudits, baseDays, baseLead)}
                  {tier('AuditPM records', 'recorded this cycle', pmAudits, pmDays, pmLead)}
                  {tier('Effective total', 'drives eligibility', eff.effAudits, eff.effDays, eff.effLead, { bold: true, rule: true })}
                </tbody>
              </table>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '3px 14px', fontSize: 13 }}>
                {[
                  { label: '≥ 4 audits', ok: eff.effAudits >= 4 },
                  { label: '≥ 10 on-site days', ok: eff.effDays >= 10 },
                  ...(gradeIsLead ? [{ label: '≥ 2 as Lead', ok: eff.effLead >= 2 }] : []),
                  { label: `≥ 75% RM/SHMS${eff.pct75val !== null ? ` (${eff.pct75val}%)` : ''}`, ok: eff.pct75ok },
                ].map(({ label, ok }) => (
                  <span key={label} style={{ color: ok ? '#27500a' : '#791F1F' }}>{ok ? '✓' : '✗'} {label}</span>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>
                The figures above cover the current accreditation cycle only. The table below lists the <b>AuditPM records</b> in that window; the baseline row is the QM-attested count when it was attested inside this cycle (otherwise it drops off and only in-cycle audits count).
              </div>
              {leadMismatch > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#791F1F', background: '#FDECEA', borderRadius: 6, padding: '6px 10px' }}>
                  ⚠ {leadMismatch} record{leadMismatch > 1 ? 's' : ''} marked “Lead Auditor”, but {auditor.name}’s roster grade is {auditor.grade || 'Auditor'} — not credited toward Lead-Auditor maintenance. Correct the record role below, or update the roster grade.
                </div>
              )}
            </div>
          )
        })()}

        {!loading && cyc.years.length > 0 && (
          <AuditBreakdown
            logs={logs}
            cycleStart={cyc.years[0]?.start}
            cycleEnd={cyc.years.length ? cyc.years[cyc.years.length - 1].end : null}
          />
        )}

        {loading ? <div style={{ color: 'var(--text3)' }}>Loading…</div> : (
          <>
            {/* Main CT17 maintenance log — finalised, countable records only (no Counts column needed). */}
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: 14 }}>
              <thead>
                <tr>{['Date', 'Client', 'Type', 'Role', 'Days', 'On-site', 'Ref', ''].map((h, i) => <th key={i} style={headStyle}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {countable.map(l => logRow(l, false))}
                {countable.length === 0 && <tr><td colSpan={8} style={{ padding: 16, textAlign: 'center', color: 'var(--text3)' }}>No audits counted in this accreditation cycle yet — a record is created when office finalises an audit (records the new bizSAFE expiry).</td></tr>}
              </tbody>
            </table>

            {/* Non-counting / historical records — explicitly separated, with a reason. Never mixed in. */}
            {nonCounting.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <button className="sm" onClick={() => setShowOther(s => !s)} style={{ fontSize: 13 }}>
                  {showOther ? '▾' : '▸'} Not counted this cycle ({nonCounting.length})
                </button>
                {showOther && (
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginTop: 8 }}>
                    <thead>
                      <tr>{['Date', 'Client', 'Type', 'Role', 'Days', 'On-site', 'Ref', 'Reason', ''].map((h, i) => <th key={i} style={headStyle}>{h}</th>)}</tr>
                    </thead>
                    <tbody>{nonCounting.map(l => logRow(l, true))}</tbody>
                  </table>
                )}
              </div>
            )}
            <button className="sm" onClick={() => setShowAdd(s => !s)}>+ Add manual record</button>
            {showAdd && (
              <div style={{ marginTop: 12, padding: 14, background: 'var(--bg2)', borderRadius: 8 }}>
                <div className="grid-2">
                  <div className="field"><label>Audit type</label>
                    <select value={form.audit_type} onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))}>
                      {AUDITOR_SCOPE_NAMES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Date</label>
                    <input type="date" value={form.audit_date} onChange={e => setForm(f => ({ ...f, audit_date: e.target.value }))} /></div>
                  <div className="field"><label>Man-days on site</label>
                    <input type="number" step="0.5" value={form.mandays} onChange={e => setForm(f => ({ ...f, mandays: e.target.value }))} /></div>
                  <div className="field"><label>Role</label>
                    <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                      <option>Auditor</option><option>Lead Auditor</option>
                    </select>
                  </div>
                  <div className="field"><label>External audit?</label>
                    <select value={form.is_external ? 'Yes' : 'No'} onChange={e => setForm(f => ({ ...f, is_external: e.target.value === 'Yes' }))}>
                      <option>Yes</option><option>No</option>
                    </select>
                  </div>
                  <div className="field"><label>Notes</label>
                    <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="primary" onClick={add}>Add record</button>
                  <button onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}


function ObservationModal({ auditor, onClose }) {
  const { profile, role, logAction } = useAuth()
  const [observations, setObservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    observation_date: new Date().toISOString().split('T')[0],
    observation_type: 'Audit observation',
    outcome: 'Satisfactory',
    remarks: '',
    follow_up_required: false,
    follow_up_notes: '',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [auditor.id])

  async function load() {
    const { data } = await supabase.from('auditor_observations')
      .select('*').eq('auditor_id', auditor.id).order('observation_date', { ascending: false })
    setObservations(data || [])
    setLoading(false)
  }

  async function save() {
    setSaving(true)
    await supabase.from('auditor_observations').insert({
      ...form,
      auditor_id: auditor.id,
      observed_by_id: profile?.id,
      observed_by_name: profile?.name || 'QM',
      observed_by_role: role,
    })
    await logAction('QM observation recorded', 'auditor', auditor.id, auditor.name, {
      type: form.observation_type, outcome: form.outcome, date: form.observation_date
    })
    setShowAdd(false)
    setForm({ observation_date: new Date().toISOString().split('T')[0], observation_type: 'Audit observation', outcome: 'Satisfactory', remarks: '', follow_up_required: false, follow_up_notes: '' })
    setSaving(false)
    load()
  }

  const outcomeColor = { Satisfactory: { bg: '#EAF3DE', color: '#27500a' }, 'Improvement needed': { bg: '#FFF3E0', color: '#854F0B' }, Unsatisfactory: { bg: '#FDECEA', color: '#791F1F' } }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>QM observations — {auditor.name}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
          Record sampling or risk-based observations. No fixed schedule required — log only when you observe, review a complaint, or identify a gap.
        </p>
        {loading ? <div style={{ color: 'var(--text3)' }}>Loading…</div> : (
          <>
            {observations.length === 0 && !showAdd && (
              <div style={{ padding: '16px 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: 13 }}>No observations recorded yet.</div>
            )}
            {observations.map(o => {
              const col = outcomeColor[o.outcome] || outcomeColor['Satisfactory']
              return (
                <div key={o.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {o.observation_date ? format(parseISO(o.observation_date), 'd MMM yyyy') : '—'} — {o.observation_type}
                      </div>
                      {o.remarks && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>{o.remarks}</div>}
                      {o.follow_up_required && o.follow_up_notes && (
                        <div style={{ fontSize: 13, color: 'var(--warn)', marginTop: 3 }}>Follow-up: {o.follow_up_notes}</div>
                      )}
                      <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>Recorded by {o.observed_by_name} ({o.observed_by_role})</div>
                    </div>
                    <span style={{ fontSize: 13, padding: '2px 9px', borderRadius: 12, fontWeight: 500, flexShrink: 0, background: col.bg, color: col.color }}>{o.outcome}</span>
                  </div>
                </div>
              )
            })}
            <button className="sm" style={{ marginTop: 12 }} onClick={() => setShowAdd(s => !s)}>+ Add observation</button>
            {showAdd && (
              <div style={{ marginTop: 12, padding: 14, background: 'var(--bg2)', borderRadius: 8 }}>
                <div className="grid-2">
                  <div className="field"><label>Date</label>
                    <input type="date" value={form.observation_date} onChange={e => setForm(f => ({ ...f, observation_date: e.target.value }))} /></div>
                  <div className="field"><label>Type</label>
                    <select value={form.observation_type} onChange={e => setForm(f => ({ ...f, observation_type: e.target.value }))}>
                      {['Audit observation', 'Performance review', 'Complaint review', 'Internal audit finding', 'Other'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Outcome</label>
                    <select value={form.outcome} onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}>
                      {['Satisfactory', 'Improvement needed', 'Unsatisfactory'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Follow-up required?</label>
                    <select value={form.follow_up_required ? 'Yes' : 'No'} onChange={e => setForm(f => ({ ...f, follow_up_required: e.target.value === 'Yes' }))}>
                      <option>No</option><option>Yes</option>
                    </select>
                  </div>
                </div>
                <div className="field"><label>Remarks</label>
                  <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Observations, findings, context…" /></div>
                {form.follow_up_required && (
                  <div className="field"><label>Follow-up notes</label>
                    <textarea value={form.follow_up_notes} onChange={e => setForm(f => ({ ...f, follow_up_notes: e.target.value }))} rows={2} /></div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save observation'}</button>
                  <button onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Competency Log — per-person history of competency events (initial training, annual review, standards/
// process/system updates, retraining). Distinct from CPD (hours) and the Audit Log (audit participation).
function CompetencyLogModal({ auditor, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  async function load() {
    setLoading(true)
    const { data } = await supabase.from('competency_event_people')
      .select('*, competency_events(*)').eq('auditor_id', auditor.id)
    const list = (data || []).filter(r => r.competency_events)
      .sort((a, b) => (b.competency_events.event_date || '').localeCompare(a.competency_events.event_date || ''))
    setRows(list); setLoading(false)
  }
  useEffect(() => { load() }, []) // eslint-disable-line
  async function markComplete(r) {
    await supabase.from('competency_event_people').update({ completed: true, completed_at: new Date().toISOString() }).eq('id', r.id)
    load()
  }
  const pendingMandatory = rows.some(r => r.competency_events?.mandatory && !r.completed)
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Competency Log — {auditor.name}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Internal competency events (training, reviews, update briefings). Bulk briefings are created from the Personnel
          list (“+ Competency event”). CPD hours and audit participation are tracked separately.
        </div>
        {pendingMandatory && (
          <div style={{ fontSize: 13, padding: '8px 12px', borderRadius: 8, background: '#FDECEA', color: '#791F1F', marginBottom: 12 }}>
            ⚠ Pending mandatory competency event — Report Approver approval is blocked until completed.
          </div>
        )}
        {loading ? <div style={{ color: 'var(--text3)', padding: 16 }}>Loading…</div>
          : rows.length === 0 ? <div style={{ color: 'var(--text3)', padding: 16, fontStyle: 'italic' }}>No competency events recorded.</div>
          : (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
              <th style={{ padding: '6px 4px' }}>Date</th><th>Type</th><th>Title</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const e = r.competency_events
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{e.event_date ? format(parseISO(e.event_date), 'd MMM yyyy') : '—'}</td>
                    <td>{eventTypeLabel(e.type)}{e.mandatory && <span style={{ color: '#791F1F', marginLeft: 4 }}>•mandatory</span>}</td>
                    <td>{e.title}</td>
                    <td>{r.completed
                      ? <span style={{ color: '#27500a' }}>Completed{r.completed_at ? ` ${format(parseISO(r.completed_at), 'd MMM')}` : ''}</span>
                      : <span style={{ color: '#854F0B' }}>Pending</span>}</td>
                    <td>{!r.completed && <button className="sm" onClick={() => markComplete(r)}>Mark complete</button>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <div className="modal-footer"><button onClick={onClose}>Close</button></div>
      </div>
    </div>
  )
}

// Bulk competency event — create ONE event and apply it to many people at once (the normal small-firm case).
function CompetencyEventModal({ people, profile, onClose }) {
  const active = (people || []).filter(p => p.active && !isTrainerOnly(p))   // trainer-only people are outside the AO scheme
  const [f, setF] = useState({ title: '', event_date: new Date().toISOString().slice(0, 10), type: 'standards_update',
    mandatory: false, notes: '', mark_completed: true })
  const [applyTo, setApplyTo] = useState('all') // all | dms | selected
  const [selected, setSelected] = useState([])
  const [saving, setSaving] = useState(false)
  function tgl(id) { setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]) }
  const targets = applyTo === 'all' ? active : applyTo === 'dms' ? active.filter(p => p.is_decision_maker) : active.filter(p => selected.includes(p.id))
  async function save() {
    if (!f.title.trim()) { toast('Enter the event title', 'warning'); return }
    if (!targets.length) { toast('Select at least one person', 'warning'); return }
    setSaving(true)
    const { data: ev, error } = await supabase.from('competency_events').insert({
      title: f.title.trim(), event_date: f.event_date, type: f.type, mandatory: f.mandatory,
      notes: f.notes || null, created_by: profile?.name || null,
    }).select().single()
    if (error) { setSaving(false); failed("Couldn't save", error.message); return }
    const now = new Date().toISOString()
    const peopleRows = targets.map(p => ({ event_id: ev.id, auditor_id: p.id,
      completed: !!f.mark_completed, completed_at: f.mark_completed ? now : null }))
    const { error: pe } = await supabase.from('competency_event_people').insert(peopleRows)
    setSaving(false)
    if (pe) { failed("Event saved, but people weren't updated", pe.message, 'Open the event and save it again'); return }
    onClose(true)
  }
  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New competency event</h2>
          <button onClick={() => onClose(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <div className="grid-2">
          <div className="field"><label>Title *</label>
            <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="e.g. CT17 Rev M briefing" /></div>
          <div className="field"><label>Date</label>
            <input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Type</label>
            <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
              {COMPETENCY_EVENT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="field"><label>Mandatory</label>
            <select value={f.mandatory ? 'Yes' : 'No'} onChange={e => setF({ ...f, mandatory: e.target.value === 'Yes' })}>
              <option>No</option><option>Yes</option>
            </select>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Mandatory + not completed blocks DM approval.</div>
          </div>
        </div>
        <div className="field"><label>Notes</label>
          <textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
        <div className="field"><label>Apply to</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['all', `All active personnel (${active.length})`], ['dms', `All active Report Approvers (${active.filter(p => p.is_decision_maker).length})`], ['selected', 'Selected people']].map(([v, l]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                padding: '5px 12px', borderRadius: 8, background: applyTo === v ? '#EAF3DE' : 'var(--bg2)',
                border: `0.5px solid ${applyTo === v ? '#639922' : 'var(--border)'}`, color: applyTo === v ? '#27500a' : 'var(--text2)' }}>
                <input type="radio" checked={applyTo === v} onChange={() => setApplyTo(v)} style={{ display: 'none' }} />{l}
              </label>
            ))}
          </div>
        </div>
        {applyTo === 'selected' && (
          <div className="field" style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
            {active.map(p => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(p.id)} onChange={() => tgl(p.id)} />
                {p.name}{p.is_decision_maker && <span style={{ fontSize: 13, color: 'var(--text3)' }}>(DM)</span>}
              </label>
            ))}
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', margin: '4px 0 8px' }}>
          <input type="checkbox" checked={f.mark_completed} onChange={() => setF({ ...f, mark_completed: !f.mark_completed })} />
          Mark as completed now (attendees were briefed). Uncheck to leave pending.
        </label>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>Will apply to <strong>{targets.length}</strong> {targets.length === 1 ? 'person' : 'people'}.</div>
        <div className="modal-footer">
          <button onClick={() => onClose(false)}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : `Create & apply to ${targets.length}`}</button>
        </div>
      </div>
    </div>
  )
}

export default function Auditors() {
  const { profile, canApprove, logAction } = useAuth()
  const [auditors, setAuditors] = useState([])
  const [observationModal, setObservationModal] = useState(null)
  const [cpdMap, setCpdMap] = useState({})           // auditor_id -> computeCpdCycle(...) result
  const [cpdCycleStart, setCpdCycleStart] = useState(null)   // firm_settings.cpd_cycle_start (company-wide)
  const [taeppMap, setTaeppMap] = useState({})       // trainer id -> computeTaepp(...) for their current cycle
  const [logMap, setLogMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [logModal, setLogModal] = useState(null)
  const [compLogModal, setCompLogModal] = useState(null)
  const [trainingModal, setTrainingModal] = useState(null)
  const [eventModal, setEventModal] = useState(false)
  const [filter, setFilter] = useState('all')

  useEffect(() => { load() }, [])

  async function load() {
    const { data: aus } = await supabase.from('auditors').select('*').order('name')
    const prevAuditors = auditors
    setAuditors(aus || [])
    if (aus?.length) {
      const ids = aus.map(a => a.id)
      // CPD is computed from the real training ledger (auditor_training), not a manually-typed table — one
      // company-wide cycle (firm_settings.cpd_cycle_start) shared by every non-WSHO auditor.
      const { data: fs } = await supabase.from('firm_settings').select('cpd_cycle_start').eq('id', 1).maybeSingle()
      const cycleStart = fs?.cpd_cycle_start || null
      setCpdCycleStart(cycleStart)
      const trainingRows = []
      // taepp_type / date_end exist only once sql/TAEPP_CPD.sql has run — fall back to the original columns so
      // the SAC CPD figures never break if the TAEPP columns are missing.
      let trCols = 'auditor_id,date_start,date_end,hours,cpd_type,taepp_type'
      for (let from = 0; ; from += 1000) {
        let { data: page, error } = await supabase.from('auditor_training').select(trCols).in('auditor_id', ids).range(from, from + 999)
        if (error && trCols.includes('taepp_type')) {
          trCols = 'auditor_id,date_start,hours,cpd_type'
          ;({ data: page } = await supabase.from('auditor_training').select(trCols).in('auditor_id', ids).range(from, from + 999))
        }
        trainingRows.push(...(page || []))
        if (!page || page.length < 1000) break
      }
      const trByAuditor = {}
      trainingRows.forEach(r => { if (!trByAuditor[r.auditor_id]) trByAuditor[r.auditor_id] = []; trByAuditor[r.auditor_id].push(r) })
      const cm = {}; ids.forEach(id => { cm[id] = computeCpdCycle(trByAuditor[id] || [], cycleStart) }); setCpdMap(cm)
      // TAEPP totals for trainers (current cycle) — CPD from the training ledger + practice hours.
      const trainers = aus.filter(a => a.is_trainer && currentTaeppCycle(a))
      const tm = {}
      if (trainers.length) {
        const { data: ph } = await supabase.from('trainer_practice_hours').select('auditor_id,date_start,date_end,hours').in('auditor_id', trainers.map(a => a.id))
        trainers.forEach(a => {
          tm[a.id] = computeTaepp(trByAuditor[a.id] || [], (ph || []).filter(p => p.auditor_id === a.id), currentTaeppCycle(a))
        })
      }
      setTaeppMap(tm)
      // Paginate past the 1000-row cap — with backfilled histories the roster's combined log exceeds it,
      // which silently starved some auditors' maintenance stats.
      const logs = []
      for (let from = 0; ; from += 1000) {
        const { data: page } = await supabase.from('auditor_audit_log').select('*').in('auditor_id', ids).range(from, from + 999)
        logs.push(...(page || []))
        if (!page || page.length < 1000) break
      }
      const lm = {}; logs.forEach(l => { if (!lm[l.auditor_id]) lm[l.auditor_id] = []; lm[l.auditor_id].push(l) }); setLogMap(lm)
      // Log eligibility changes
      const changes = []
      for (const a of (aus || [])) {
        if (!a.is_auditor) continue   // auditor eligibility does not apply to Report Approver-only personnel
        const prev = (prevAuditors || []).find(p => p.id === a.id)
        if (!prev) continue
        const prevElig = calcEligibility(prev, cm[a.id], lm[a.id])
        const currElig = calcEligibility(a, cm[a.id], lm[a.id])
        if (prevElig.eligible !== currElig.eligible) {
          changes.push({ auditor_id: a.id, auditor_name: a.name,
            previous_status: prevElig.eligible ? 'Eligible' : 'Not Eligible',
            new_status: currElig.eligible ? 'Eligible' : 'Not Eligible',
            reason: currElig.eligible ? 'All checks now passing' : currElig.reasons.join('; ') })
        }
      }
      if (changes.length > 0) await supabase.from('auditor_eligibility_log').insert(changes)
    }
    setLoading(false)
  }

  async function handleDelete(auditor) {
    if (!window.confirm(`Delete auditor "${auditor.name}"? This cannot be undone.`)) return
    const { error } = await supabase.from('auditors').delete().eq('id', auditor.id)
    if (error) { failed("Couldn't delete", error.message); return }
    setAuditors(as => as.filter(a => a.id !== auditor.id))
    await logAction('Auditor deleted', 'auditor', auditor.id, auditor.name, {})
  }

  function handleSave(saved) {
    setAuditors(as => as.some(a => a.id === saved.id) ? as.map(a => a.id === saved.id ? saved : a) : [...as, saved])
    setModal(null)
  }

  // TAEPP shortfall needing attention: an active trainer in the final 6 months of their cycle who is still short.
  const taeppIssue = a => a.active && a.is_trainer && !!taeppMap[a.id] && !taeppMap[a.id].met && taeppInFinalSixMonths(currentTaeppCycle(a))

  // 4-year maintenance needing attention: lapsed, or still building but short with ≤ 12 months of the cycle left.
  const maintIssue = a => {
    if (!a.is_auditor || !a.active) return false
    const m = computeMaintenance(a, logMap[a.id], cpdMap[a.id])
    return m.state === 'lapsed' || maintAtRisk(a, m)
  }

  const filtered = auditors.filter(a => {
    if (filter === 'issues') {
      if (!a.active) return false                       // inactive people never need attention
      const expWsh = a.wsh_auditor_expiry && differenceInDays(parseISO(a.wsh_auditor_expiry), new Date()) <= 90
      const expWsho = a.wsho_status && a.wsho_licence_expiry && differenceInDays(parseISO(a.wsho_licence_expiry), new Date()) <= 90
      // CPD issue = the shared CPD limb is unsatisfied (no valid WSHO AND no sufficient CPD record). WSHO
      // auditors are never flagged. Same source as eligibility / CPD summaries.
      const cpdIssue = a.is_auditor && !computeMaintenance(a, logMap[a.id], cpdMap[a.id]).cpdLimbOk
      const notApproved = a.is_auditor && a.approval_status !== 'Approved'
      // Same flags as the red / amber chips under the name: competence review overdue or due, retraining required.
      const reviewDue = ['overdue', 'due'].includes(competenceReviewStatus(a)?.state)
      return expWsh || expWsho || cpdIssue || notApproved || maintIssue(a) || taeppIssue(a) || reviewDue || !!a.retraining_required
    }
    if (filter === 'trainers') return a.active && a.is_trainer
    if (filter === 'inactive') return !a.active
    return a.active
  })

  // Clean loading state — don't render the summary cards + table against empty data (the blank-row flash).
  if (loading) return (
    <div>
      <div className="page-header"><h1>Personnel</h1></div>
      <div className="card" style={{ color: 'var(--text3)', padding: 24 }}>Loading personnel…</div>
    </div>
  )

  return (
    <div>
      {modal && <AuditorModal auditor={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} />}
      {logModal && <AuditLogModal auditor={logModal} cpdCycleStart={cpdCycleStart} onClose={() => { setLogModal(null); load() }} />}
      {observationModal && <ObservationModal auditor={observationModal} onClose={() => setObservationModal(null)} />}
      {compLogModal && <CompetencyLogModal auditor={compLogModal} onClose={() => { setCompLogModal(null); load() }} />}
      {trainingModal && <TrainingModal auditor={trainingModal} cycleStart={cpdCycleStart} onClose={() => { setTrainingModal(null); load() }} />}
      {eventModal && <CompetencyEventModal people={auditors} profile={profile} onClose={(changed) => { setEventModal(false); if (changed) load() }} />}

      <div className="page-header">
        <h1>Personnel</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {canApprove && <button onClick={() => setEventModal(true)}>+ Competency event</button>}
          {canApprove
            ? <button className="primary" onClick={() => setModal('new')}>+ Add person</button>
            : <span style={{ fontSize: 13, color: 'var(--text3)', alignSelf: 'center' }}>View only — contact the Quality Manager to make changes.</span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['all', 'Active'], ['issues', 'Needs attention'], ['monitoring', 'QM monitoring'], ['trainers', 'Trainers'], ['inactive', 'Inactive']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ background: filter === v ? 'var(--brand)' : 'var(--bg)', color: filter === v ? '#fff' : 'var(--text)', borderColor: filter === v ? 'var(--brand)' : 'var(--border2)', fontSize: 13 }}>{l}</button>
        ))}
      </div>

      {filter === 'monitoring' && (() => {
        // Auditor eligibility applies to AUDITORS only (same rule as the roster's Eligibility badge) — never to
        // Report Approver-only, office or trainer-only people.
        const notEligible = auditors.filter(a => a.active && a.is_auditor && !calcEligibility(a, cpdMap[a.id], logMap[a.id]).eligible)
        const expiringSoon = auditors.filter(a => {
          if (!a.active || !a.is_auditor) return false
          const w = a.wsh_auditor_expiry && differenceInDays(parseISO(a.wsh_auditor_expiry), new Date()) <= 90
          const ws = a.wsho_status && a.wsho_licence_expiry && differenceInDays(parseISO(a.wsho_licence_expiry), new Date()) <= 90
          return w || ws
        })
        // Same shared logic as the eligibility badge → the page cannot contradict itself.
        const cpdGaps = auditors.filter(a => a.is_auditor && a.active && !computeMaintenance(a, logMap[a.id], cpdMap[a.id]).cpdLimbOk)
        const maintGaps = auditors.filter(maintIssue)   // lapsed, or short with ≤ 12 months of the cycle left
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, borderLeft: '3px solid var(--brand)' }}>
              System-driven continuous monitoring. QM review is triggered by system flags only — no fixed schedule required.
            </div>
            {[
              { title: 'Not eligible for assignment', items: notEligible, color: '#FDECEA', textColor: '#791F1F', getDetail: a => calcEligibility(a, cpdMap[a.id], logMap[a.id]).reasons.join(' · ') },
              { title: 'Certifications expiring within 90 days', items: expiringSoon, color: '#FFF3E0', textColor: '#854F0B', getDetail: a => [a.wsh_auditor_expiry && differenceInDays(parseISO(a.wsh_auditor_expiry), new Date()) <= 90 ? `WSH approval: ${format(parseISO(a.wsh_auditor_expiry), 'd MMM yyyy')}` : null, a.wsho_status && a.wsho_licence_expiry && differenceInDays(parseISO(a.wsho_licence_expiry), new Date()) <= 90 ? `WSHO licence: ${format(parseISO(a.wsho_licence_expiry), 'd MMM yyyy')}` : null].filter(Boolean).join(' · ') },
              { title: 'CPD gaps (AO Scheme cycle)', items: cpdGaps, color: '#FFF3E0', textColor: '#854F0B', getDetail: a => {
                  const c = cpdMap[a.id]
                  if (!c || c.state === 'no_cycle') return 'No CPD record and no valid WSHO (set the accreditation cycle under Settings)'
                  const h = n => (Number(n) || 0).toFixed(1).replace(/\.0$/, '')
                  const b = c.binding   // the latest completed checkpoint that compliance is judged against
                  if (b && !b.met) {
                    const parts = [
                      b.remainingAcc > 0 ? `Accredited ${h(b.accCum)}/${b.reqCum}h (short ${h(b.remainingAcc)}h)` : `Accredited ${h(b.accCum)}/${b.reqCum}h ✓`,
                      b.remainingNon > 0 ? `Non-accredited ${h(b.nonCum)}${b.offsetUsed > 0 ? `+${h(b.offsetUsed)}` : ''}/${b.reqCum}h (short ${h(b.remainingNon)}h)` : `Non-accredited ${h(b.nonCum)}${b.offsetUsed > 0 ? `+${h(b.offsetUsed)}` : ''}/${b.reqCum}h ✓`,
                    ]
                    return `Checkpoint end Yr ${b.index}: ${parts.join(' · ')} (cumulative)`
                  }
                  return 'No sufficient CPD record and no valid WSHO'
                } },
              { title: '4-year maintenance gaps', items: maintGaps, color: '#FDECEA', textColor: '#791F1F', getDetail: a => {
                  const m = computeMaintenance(a, logMap[a.id], cpdMap[a.id])
                  const due = m.cycleEnd ? format(addDays(m.cycleEnd, -1), 'd MMM yyyy') : null
                  const needs = maintShortfalls(a, m).join(' · ')
                  return m.state === 'lapsed'
                    ? `Lapsed — ${needs || m.reasons.join(' · ')}`
                    : `Short, due ${due}: needs ${needs}`
                } },
            ].map(({ title, items, color, textColor, getDetail }) => (
              <div key={title} style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 14, overflow: 'hidden' }}>
                <div style={{ background: items.length > 0 ? color : '#EAF3DE', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500, fontSize: 13, color: items.length > 0 ? textColor : '#27500a' }}>{title}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: items.length > 0 ? textColor : '#27500a' }}>{items.length === 0 ? '✓ All clear' : items.length}</span>
                </div>
                {items.length > 0 && items.map(a => (
                  <div key={a.id} style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>{a.name}</span>
                      <span style={{ color: 'var(--text2)', marginLeft: 8, fontSize: 13 }}>{a.is_auditor ? a.grade : a.is_decision_maker ? 'Report Approver' : a.is_staff ? (a.staff_designation || 'Office / Management') : a.is_trainer ? 'Trainer' : ''}</span>
                      <div style={{ fontSize: 13, color: textColor, marginTop: 2 }}>{getDetail(a)}</div>
                    </div>
                    {canApprove && (
                      <button className="sm" onClick={() => setObservationModal(a)}>Log observation</button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      })()}

      {filter !== 'monitoring' && <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table style={{ width: '100%' }}>
            <colgroup>
              <col style={{ width: 200 }} />{/* Name */}
              <col style={{ width: 90 }} />{/* Grade */}
              <col style={{ width: 130 }} />{/* Eligibility */}
              <col style={{ width: 140 }} />{/* Approval */}
              <col style={{ width: 120 }} />{/* WSHO expiry — nowrap */}
              <col style={{ width: 90 }} />{/* CPD — nowrap */}
              <col style={{ width: 210 }} />{/* 4yr maintenance — fixed so it doesn't stretch and push Actions to the edge */}
              <col style={{ width: 250 }} />{/* Actions — sits right after 4yr maintenance, left-aligned */}
              <col />{/* spacer — absorbs remaining width so the header shading + row lines reach the card edge (no empty white band); real columns keep their fixed widths */}
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Eligibility</th>
                <th>Approval</th>
                <th>WSHO expiry</th>
                <th>CPD cycle</th>
                <th>4yr maintenance</th>
                <th></th>
                <th></th>{/* spacer */}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>No auditors</td></tr>}
              {filtered.map((a, idx) => {
                const cpd = cpdMap[a.id]
                const cpds = cpdBadge(cpd, isWshoValid(a))
                const maint = computeMaintenance(a, logMap[a.id], cpdMap[a.id])   // same source as the eligibility badge
                return (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{a.name}</div>
                      {a.email && <div style={{ fontSize: 13, color: 'var(--text2)' }}>{a.email}</div>}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
                        {a.is_external && <span style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, background: '#f0f0f0', color: '#666' }}>External</span>}
                        {(() => { const r = competenceReviewStatus(a); return r && r.state !== 'ok'
                          ? <span style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, background: r.state === 'overdue' ? '#FDECEA' : '#FFF3E0', color: r.state === 'overdue' ? '#791F1F' : '#854F0B' }}>{r.label}</span> : null })()}
                        {a.retraining_required && <span style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, background: '#FDECEA', color: '#791F1F' }}>Retraining</span>}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{a.is_auditor ? a.grade : a.staff_designation ? a.staff_designation : a.is_trainer ? 'Trainer' : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {a.is_auditor && (() => { const e = calcEligibility(a, cpdMap[a.id], logMap[a.id]); return <EligibilityBadge eligible={e.eligible} reasons={e.reasons} /> })()}
                        {a.is_decision_maker && <span title="Authorised Report Approver (see Approval tab)" style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, background: '#EDE7F6', color: '#4527A0' }}>Report Approver</span>}
                        {a.is_staff && <span title="Office / management personnel (clause 7.4)" style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, background: '#E3F2FD', color: '#0D47A1' }}>Office / Management</span>}
                        {a.is_trainer && (() => {
                          const t = taeppMap[a.id]
                          const short = taeppIssue(a)
                          return <span title={t ? `TAEPP: CPD ${fmtHrs(t.nae)}/${TAEPP_REQ.nae}h · IAL ${fmtHrs(t.ial)}/${TAEPP_REQ.ial}h · Practice ${fmtHrs(t.practice)}/${TAEPP_REQ.practice}h` : 'TAEPP trainer — no cycle set yet'}
                            onClick={() => setTrainingModal(a)}
                            style={{ fontSize: 13, padding: '1px 6px', borderRadius: 10, cursor: 'pointer', background: short ? '#FFF3E0' : '#E0F2F1', color: short ? '#854F0B' : '#00695C' }}>
                            {short ? 'Trainer · TAEPP short' : 'Trainer'}</span>
                        })()}
                        {!a.is_auditor && !a.is_decision_maker && !a.is_staff && !a.is_trainer && <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {a.is_auditor && (
                          <span title="Auditor approval (QM)" style={{ fontSize: 13, padding: '2px 8px', borderRadius: 12,
                            background: a.approval_status === 'Approved' ? '#EAF3DE' : a.approval_status === 'Rejected' ? '#FDECEA' : '#FFF3E0',
                            color: a.approval_status === 'Approved' ? '#27500a' : a.approval_status === 'Rejected' ? '#791F1F' : '#854F0B' }}>
                            {a.approval_status || 'Pending'}
                          </span>
                        )}
                        {a.is_decision_maker && (
                          <span title="Report Approver authorisation (Approval tab)" style={{ fontSize: 13, padding: '2px 8px', borderRadius: 12,
                            background: a.dm_authorised ? '#EAF3DE' : '#FFF3E0', color: a.dm_authorised ? '#27500a' : '#854F0B' }}>
                            {a.dm_authorised ? (a.is_auditor ? 'DM authorised' : 'Authorised') : (a.is_auditor ? 'DM pending' : 'Pending')}
                          </span>
                        )}
                      </div>
                    </td>
                    {/* WSHO expiry — plain single-line text; warning colour only when expired / expiring soon */}
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {(() => {
                        if (!a.is_auditor || !a.wsho_status || !a.wsho_licence_expiry) return <span style={{ color: 'var(--text3)' }}>—</span>
                        const d = differenceInDays(parseISO(a.wsho_licence_expiry), new Date())
                        if (d < 0) return <span style={{ color: '#791F1F', fontWeight: 500 }}>Expired</span>
                        if (d <= 60) return <span style={{ color: '#854F0B' }}>Expires in {d}d</span>
                        return <span style={{ color: 'var(--text2)' }}>{format(parseISO(a.wsho_licence_expiry), 'd MMM yyyy')}</span>
                      })()}
                    </td>
                    {/* CPD cycle — plain text; WSHO / Met neutral, Partial amber, Deficient/No record red. Click opens
                        Training records (the single source for CPD — there is no separate CPD screen anymore). */}
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {(() => {
                        if (!a.is_auditor) return <span style={{ color: 'var(--text3)' }}>—</span>
                        const l = cpds.label
                        if (l === 'N/A (WSHO)') return <span style={{ color: 'var(--text2)' }}>WSHO</span>
                        if (l === 'Met') return <span style={{ color: 'var(--text2)' }}>Met</span>
                        const isPartial = l === 'Partial'
                        return <span style={{ color: isPartial ? '#854F0B' : '#791F1F', cursor: 'pointer' }} onClick={() => setTrainingModal(a)}>{l}</span>
                      })()}
                    </td>
                    {/* 4yr maintenance — plain single-line text; Compliant green, Lapsed red, else neutral */}
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {(() => {
                        if (!a.is_auditor) return <span style={{ color: 'var(--text3)' }}>—</span>
                        const s = maint.state
                        // "building" = within first 4-year cycle (not yet due). Split the label: an auditor whose
                        // EFFECTIVE totals already clear every threshold reads "On track · review due <approval+4y>"
                        // rather than a misleading "233/4" fraction; someone genuinely short still shows progress.
                        const gradeIsLead = a.grade === 'Lead Auditor'
                        const metAll = maint.effAudits >= 4 && maint.effDays >= 10 && (!gradeIsLead || maint.effLead >= 2) && maint.pct75ok
                        let reviewDue = ''
                        if (maint.cycleEnd) reviewDue = format(addDays(maint.cycleEnd, -1), 'd MMM yyyy')
                        // Short with ≤ 12 months left → say so plainly (amber) instead of a neutral "Building".
                        const atRisk = maintAtRisk(a, maint)
                        const needs = maintShortfalls(a, maint)
                        const buildingTxt = metAll
                          ? `On track${reviewDue ? ` · review due ${reviewDue}` : ''}`
                          : atRisk ? `Short · due ${reviewDue}`
                          : `Building (${maint.effAudits}/4, ${maint.effDays}d)`
                        const txt = s === 'compliant' ? 'Compliant'
                          : s === 'building' ? buildingTxt
                          : s === 'lapsed' ? 'Lapsed'
                          : 'Set Baseline'
                        const title = s === 'building' && metAll
                          ? `Thresholds already met (${maint.effAudits} audits · ${maint.effDays} days); first 4-year review due ${reviewDue || '—'}`
                          : s === 'building' && needs.length ? `Needs ${needs.join(' · ')} by ${reviewDue || 'cycle end'}`
                          : maint.reasons.join(' · ')
                        const color = s === 'lapsed' ? '#791F1F' : s === 'compliant' ? '#27500a' : atRisk ? '#854F0B' : 'var(--text2)'
                        return <span title={title} style={{ color, cursor: 'pointer' }} onClick={() => setLogModal(a)}>{txt}</span>
                      })()}
                    </td>
                    {/* Actions — last column, now visible without horizontal scroll (Audit types removed) */}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="sm" onClick={() => setModal(a)} style={{ marginRight: 4 }}>View</button>
                      {canApprove && <>
                        <RowMoreMenu dropUp={idx >= filtered.length - 2} items={[
                          ...(!isTrainerOnly(a) ? [
                            { label: 'Audit Log', onClick: () => setLogModal(a) },
                            { label: 'Competency', onClick: () => setCompLogModal(a) },
                          ] : []),
                          { label: 'Training (incl. CPD)', onClick: () => setTrainingModal(a) },
                        ]} />
                        {!isTrainerOnly(a) && <button className="sm" onClick={() => setObservationModal(a)} style={{ marginRight: 4 }}>Observe</button>}
                        <button className="sm" onClick={() => handleDelete(a)} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>Delete</button>
                      </>}
                    </td>
                    <td />{/* spacer — carries the row divider to the card edge */}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  )
}
